/** EN translations for Jobolar */
const en = {
// ─── Navigation & Header ───────────────────────────────
nav: {
  how: "How it works",
  features: "Features",
  reviews: "Reviews",
  pricings: "Pricing",
  faq: "FAQ",
  contact: "Contact Us",
  login: "Log In",
  signup: "Sign Up",
  logout: "Logout",
  view_account: "View account",
  toggle_nav: "Toggle navigation",
  primary_navigation: "Primary navigation",
  mobile_navigation: "Mobile navigation",
  account_menu: "Account menu",
  home: "Home",
  about: "About",
  ai_assistant: "AI Assistant",
},

ai_chatty: {
  launcher: "AI Chatty Buddy",
  subtitle: "Your JOBolar.Ai assistant",
  badge: "AI Assistant",
  minimize: "Minimize AI Chatty Buddy",
  close: "Close AI Chatty Buddy",
  reopen: "Reopen AI Chatty Buddy",
},

// ─── Hero / Landing ────────────────────────────────────
hero: {
  title_prefix: "Auto-apply to jobs with",
  sub: "Connect your resume once. JOBolar finds roles locally and worldwide, auto-tailors your resume, generates customized AI cover letters, and applies for you—while you track everything in one dashboard.",
  cta_get_started: "Get started free",
  cta_see_how: "See how it works",
  sr_about: "About JOBolar",
  sr_partnerships: "Partnerships and Affiliates",
  trust_line: "Trusted by job seekers seeking roles both locally and worldwide.",
  dash_preview: "Dashboard Preview",
  jobs_applied: "Jobs Applied",
  today: "Today",
  this_week: "This week",
  bullet_1: "AI-tailored cover letters",
  bullet_2: "Auto-fill applications",
  bullet_3: "Track outcomes",
  bullet_4: "Platform compliance",
  welcome_back: "Welcome back 👋",
  welcome_back_sub: "Pick up where you left off. Continue your application or jump to your dashboard.",
  continue_application: "Continue your application",
  view_dashboard: "View dashboard",
  // Interview Assistant promo card
  ic_promo_badge: "New",
  ic_promo_title: "Interview Assistant",
  ic_promo_desc: "Practice interviews with live AI feedback — tailored to your resume and target role. Prepare for your next opportunity before it arrives.",
  ic_promo_cta: "Try Interview Assistant",
},

// ─── How It Works ──────────────────────────────────────
howitworks: {
  title: "How it works",
  subtitle: "Three simple steps to automate your international job search.",
  step1_title: "Connect",
  step1_desc: "Sign in with your Gmail account and upload your resume. JOBolar securely links to your inbox to begin sending applications on your behalf.",
  step2_title: "Automate",
  step2_desc: "JOBolar automatically sends your resume and personalized cover letters to matching roles — locally and worldwide — based on your preferences.",
  step3_title: "Track",
  step3_desc: "Monitor daily email applications, track which companies received your resume, and see real-time analytics from your personal dashboard.",
  trust_privacy: "Privacy-first:",
  trust_privacy_desc: "Your credentials are never stored. Authentication is handled via Google OAuth.",
  trust_email: "Two application paths:",
  trust_email_desc: "Email Auto Apply handles eligible postings with a valid public employer or recruiter email, while the Chrome extension can fill and submit supported browser forms.",
  trust_setup: "Simple setup:",
  trust_setup_desc: "Connect your Gmail, upload your resume, set your preferences — and JOBolar takes care of the rest.",
},

// ─── Features ──────────────────────────────────────────
features: {
  title: "Features",
  feature1_title: "Affordable Worldwide",
  feature1_desc: "Pricing designed for job seekers and professionals across all regions.",
  feature2_title: "Apply Locally Or Abroad, From Home",
  feature2_desc: "Find and apply for roles in your own country or abroad — all from your current location.",
  feature3_title: "Multi-Resume Management",
  feature3_desc: "Create and manage up to 6 tailored resumes for different job types, industries, or regions.",
  feature4_title: "AI Cover Letters",
  feature4_desc: "Generate personalized cover letters for every application using built-in AI.",
  feature5_title: "Real-Time Dashboard",
  feature5_desc: "Track your applications, monitor success rates, and view analytics in one central dashboard.",
  feature6_title: "Email & Browser Applications",
  feature6_desc: "Use scheduled Email Auto Apply for eligible email postings or the Chrome extension for supported browser forms.",
  feature7_title: "Platform Compliance",
  feature7_desc: "JOBolar follows Google API policies, including limited use requirements and data handling standards.",
  feature8_title: "Privacy By Design",
  feature8_desc: "Your data stays minimal and secure. We collect only what's needed and never sell your information.",
  feature9_title: "Built-In AI Buddy",
  feature9_desc: "Chat with the AI buddy for guidance across profiles, analytics, pricing, and FAQs.",
  sr_smart_apply: "Smart Apply Assist",
  sr_resume_builder: "Resume Builder",
  sr_cover_letter: "Cover Letter AI",
},

// ─── Reviews ───────────────────────────────────────────
reviews: {
  title: "What Jobolar users say",
  subtitle: "Hand-picked stories from early international job seekers using Jobolar.",
  read_more: "Read more",
  show_less: "Show less",
  google_reviews_title: "Top Google Reviews",
  google_reviews_desc: "Top Google reviews, refreshed weekly. JOBolar is committed to transparency.",
  google_reviews_empty: "Google reviews will appear here once we receive our first review.",
  see_google_listing: "See our Google listing",
  anonymous: "Anonymous",
  google_review: "Google review",
  jobolar_user: "Jobolar user",
},

// ─── Footer ────────────────────────────────────────────
footer: {
  description: "Automate job applications, tailor documents, and stay on top of every opportunity with the JOBolar.Ai toolkit.",
  product: "Product",
  resources: "Resources",
  legal: "Legal",
  social_media: "Social Media",
  contact: "Contact",
  auto_apply: "Auto-Apply",
  smart_apply: "Smart Apply Assist",
  resume_builder: "Resume Builder",
  cover_letter_ai: "Cover Letter AI",
  future_phases: "Current Products",
  how_it_works: "How it Works",
  reviews: "Reviews",
  faq: "FAQ",
  about: "About",
  terms: "Terms of Service",
  privacy: "Privacy Policy",
  refund: "Refund Policy",
  connect_live: "Connect with us live",
  rights: "All rights reserved.",
},

// ─── Auth / Login / Signup ──────────────────────────────
auth: {
  welcome_back: "Welcome back",
  sign_in_desc: "Sign in with Google to jump right into your onboarding workspace.",
  why_continue: "Why continue with JOBolar?",
  benefit_analytics: "Real-time analytics synced from your Firestore collection keep your pipeline visible from this onboarding hub.",
  benefit_profiles: "Create up to six tailored profiles so your resumes and applications stay perfectly targeted.",
  benefit_ai: "Chat with the built-in AI buddy for guidance across profiles, analytics, pricing, and FAQs.",
  continue_google: "Continue with Google",
  continue_gmail: "Continue with Gmail",
  loading: "Loading…",
  connecting: "Connecting…",
  login_retry_error: "Couldn’t connect to Google. Please try again.",
  redirecting: "Redirecting…",
  sign_out: "Sign out",
  access_denied: "Access denied. Your Google account isn't permitted for sign in.",
  oauth_not_linked: "This email is linked to a different provider. Try that provider.",
  auth_config_error: "Auth configuration error. Please try again later.",
  something_wrong: "Something went wrong. Please try again.",
  create_account: "Create your JOBolar.Ai account",
  signup_with_google: "Sign up with Google",
  gmail_only_info: "Connecting Gmail is optional and is needed only for Email Auto Apply. Job search, browser assistance, resume tools, and Interview Assistant do not require Gmail sending access.",
  before_signup: "Before you sign up",
  choose_gmail: "Choose a Gmail account",
  choose_gmail_desc: "You may use your existing Gmail account or create a new one. We recommend using a professional-sounding Gmail for best results.",
  professional_examples: "Professional Gmail examples",
  create_gmail: "Create Gmail",
  why_gmail: "Why Gmail?",
  why_gmail_desc: "For Email Auto Apply, Jobolar uses Google's secure Gmail API to compose and send eligible applications. OAuth keeps your password private, and you can disconnect Gmail at any time.",
  agree_terms: "By continuing, you agree to Jobolar's Terms of Service and Privacy Policy.",
  gmail_only_error: "Only @gmail.com addresses are supported.",
},

// ─── Pre-Signup Coverage Modal ─────────────────────────
signup: {
  close_label: "Go to Jobolar homepage",
  badge: "Before you sign up\u2026",
  heading: "Check if JOBolar Ai currently supports your target countries",
  description: "Email Auto Apply works only for eligible postings with a valid public employer or recruiter email in supported countries. For other supported applications, the Jobolar Chrome extension can help fill browser forms.",
  step1: "1. Choose your target continent",
  phase1_coverage: "Email Auto Apply coverage: {count} countries across {n} continents.",
  search_placeholder: "Search countries in {name}\u2026",
  step2: "2. Countries supported in",
  phase1_email: "Email Auto Apply submits only eligible email postings. The Chrome extension is a separate browser tool that can fill and submit supported forms with user controls.",
  no_matches: "No matches.",
  transparency_title: "Transparency (without exposing sources)",
  transparency_p1: "Email Auto Apply only submits eligible postings with a valid public employer or recruiter email. The Chrome extension separately supports compatible browser forms; discovery sources and automation logic stay private.",
  transparency_p2: "Want a new country? Tell us on the right \u2014 it goes to",
  not_seeing_title: "Not seeing your country yet?",
  not_seeing_desc: "Tell us which country or job board you\u2019d like JOBolar Ai to support. We\u2019ll send this directly to {email} and use it to shape our roadmap.",
  label_countries: "Countries you\u2019d like us to support",
  placeholder_countries: "e.g. USA, Australia, New Zealand",
  label_email: "Your email",
  placeholder_email: "e.g. jane@example.com",
  email_helper: "Provide your email so the JOBolar team can follow up.",
  label_jobboard: "Job board or link (optional)",
  placeholder_jobboard: "e.g. https://examplejobboard.com",
  sending: "Sending\u2026",
  send_request: "Send request to JOBolar Ai team",
  submit_success: "Thanks \u2014 your request has been sent to the JOBolar team. Redirecting to the homepage\u2026",
  submit_error: "Something went wrong. Please try again.",
  validate_country: "Please enter at least one country.",
  validate_email: "Please enter a valid email address. Email is required.",
  roadmap_title: "Explore current products",
  roadmap_desc: "See how Jobolar's live job search, email automation, Chrome extension, resume tools, and Interview Assistant work together.",
  roadmap_btn: "View current products",
  footer_info: "Once you\u2019ve checked that your countries are supported, you can continue to create your JOBolar Ai account.",
  go_back: "Go back",
  continue_signup: "Yes, my countries are supported \u2013 continue to sign up",
  continent_africa: "Africa",
  continent_europe: "Europe",
  continent_north_america: "North America",
},

// ─── Onboarding ────────────────────────────────────────
onboarding: {
  loading_pricings: "Loading pricings…",
  loading_gmail: "Loading Gmail consent…",
  tab_profile: "Profile",
  tab_resumes: "Resume Profiles",
  tab_certificates: "Upload Certificates",
  tab_preferences: "Preferences",
  tab_gmail: "Gmail Consent",
  tab_pricing: "Pricing",
  // Profile Tab
  identity_details: "Identity Details",
  identity_desc: "Tell us how you'd like to be introduced across automations and touchpoints.",
  first_name: "First Name",
  middle_name: "Middle Name",
  last_name: "Last Name",
  preferred_name: "Preferred First Name",
  phone_number: "Phone Number",
  legal_first_name: "Legal first name",
  use_same_name: "Use the same name shown on your resume and professional documents.",
  optional: "Optional",
  formal_correspondence: "We will only use this when preparing formal correspondence.",
  surname: "Surname",
  keep_consistent: "Keep things consistent with your legal identity.",
  what_call_you: "What should we call you?",
  name_in_comms: "This is the name we will use in emails, reminders, and check-ins.",
  phone_placeholder: "+1234567890",
  phone_helper: "Include country code (must start with +). Example: +1234567890",
  location_availability: "Location & Availability",
  location_desc: "Perfect matches begin with knowing where you're happiest to work.",
  current_state: "Current State or Province",
  state_placeholder: "e.g., California, Lagos State",
  current_country: "Current Country",
  select_country: "Select a country",
  state_helper: "Share the region listed on your resume to align with local opportunities.",
  country_placeholder: "e.g., United States, Nigeria",
  country_helper: "We curate results with visa and relocation preferences in mind.",
  professional_links: "Professional Links",
  professional_links_desc: "Add public profiles JOBolar.Ai can reuse when building Base, Smart, and Deep résumés.",
  linkedin_url: "LinkedIn URL",
  linkedin_url_helper: "Used as a professional contact link when you choose to include it in a résumé.",
  github_url: "GitHub URL",
  github_url_helper: "Useful for technical work, code samples, and open-source contributions.",
  website_url: "Portfolio or Personal Website",
  website_url_helper: "Add a portfolio, personal website, or other professional home page.",
  research_profile_url: "Research Profile URL",
  research_profile_url_helper: "Google Scholar, ORCID, ResearchGate, or another public research profile.",
  profile_url_invalid: "Enter a complete URL beginning with http:// or https://.",
  signature_profile: "Signature profile",
  craft_presence: "Craft a presence that feels bespoke",
  profile_intro: "A polished profile unlocks concierge-style recommendations and tailored automations. Share the details that best represent you and we will sprinkle them across your JOBolar experience.",
  profile_completeness: "Profile Completeness",
  complete_100: "100% Complete",
  laptop_name: "Laptop Name",
  laptop_placeholder: "e.g., Leo's MacBook Pro",
  laptop_helper: "This helps us identify your device for automation sessions.",
  save_profile: "Save Profile",
  saving: "Saving…",
  profile_saved: "Profile saved successfully!",
  profile_save_error: "Failed to save profile. Please try again.",
  // Preferences Tab
  preferences_title: "Work Preferences",
  preferences_desc: "Tell us about the kind of roles and locations you're interested in.",
  job_title: "Job Title",
  job_title_placeholder: "e.g., Software Engineer",
  country: "Country",
  preferred_companies: "Preferred Companies (comma separated)",
  companies_placeholder: "e.g., Google, Microsoft, Amazon",
  states_to_apply: "States to Apply (comma separated)",
  states_placeholder: "e.g., California, New York, Texas",
  save_preferences: "Save Preferences",
  preferences_saved: "Preferences updated successfully!",
  preferences_error: "Failed to save preferences. Please try again.",
  coverage_based_on: "Based on: First Name, Last Name, Current State or Province, Current Country, and Phone Number.",
  // Gmail Consent Tab
  gmail_consent_title: "Gmail consent & connect",
  gmail_enable_title: "Enable concierge sending — safely",
  gmail_enable_desc: "Allow JOBolar to compose and send application emails on your behalf. You can revoke access anytime.",
  gmail_optional_badge: "Optional setup",
  gmail_optional_title: "Only needed for Email Auto Apply",
  gmail_optional_desc: "Connect Gmail only if you want Jobolar to send eligible email applications in the {count} currently supported countries. The posting must also include a valid public employer or recruiter application email.",
  gmail_optional_countries_link: "View the {count} supported countries",
  gmail_optional_skip: "If your country is not listed—or you do not want Email Auto Apply—you can skip this step. Job Search, Chrome Smart Apply on supported sites, résumé tools, and Interview Assistant do not require this Gmail connection.",
  consent_completeness: "Consent & connection completeness",
  consent_granted: "Consent granted",
  connected: "Connected",
  encrypted_token: "Encrypted token stored",
  step1_review: "Step 1: Review & consent",
  step2_enable: "Step 2: Click the \"Enable Gmail sending\" Button Below 👇",
  consent_text: "I consent to JOBolar composing and sending job application emails from my Gmail on my behalf.",
  what_we_store: "What we store:",
  what_we_store_desc: "An encrypted OAuth refresh token — no password, no direct inbox access.",
  limited_use: "Limited Use:",
  limited_use_desc: "JOBolar only composes and sends emails you have approved. It does not read, modify, or delete anything else.",
  enable_gmail: "Enable Gmail Sending",
  disable_gmail: "Disable Gmail Sending",
  gmail_disabled: "Gmail sending disabled successfully. You can re-enable it anytime.",
  gmail_enabled: "Gmail sending enabled successfully!",
  gmail_error: "Failed to update Gmail consent. Please try again.",
  // Gmail Consent Tab (extended)
  gmail_consent_full: "I consent to JOBolar composing and sending job application emails from my Gmail account on my behalf. I can revoke this access anytime in JOBolar or in",
  gmail_google_settings: "my Google Account settings",
  gmail_what_we_store_full: "We store an encrypted Gmail refresh token on our servers, used only to send job-application emails you trigger. We do not store your Gmail password, do not read your inbox, and we delete the token immediately when you disconnect Gmail or revoke access.",
  gmail_limited_use_full: "We do not use Gmail data to train models or for advertising; no human reviews your Gmail data without your consent.",
  gmail_first_time: "If this is your first time, Google will briefly ask for permission to send email. You can revoke access anytime.",
  gmail_review_legal: "Please also review our",
  gmail_review: "Review",
  gmail_consent_label: "Consent",
  gmail_granted: "Granted",
  gmail_not_granted: "Not granted",
  gmail_connected_label: "Connected",
  gmail_token_label: "Encrypted token stored (server)",
  gmail_token_yes: "Yes (encrypted at rest, used only for sending)",
  gmail_completion_label: "Gmail Consent Completion",
  gmail_completion_based: "Based on: Gmail connection enabled and send permission granted.",
  gmail_disable_title: "Disable Gmail Sending?",
  gmail_disable_desc: "By disabling Gmail sending, you revoke JOBolar's ability to send job application emails from your Gmail account.",
  gmail_disable_what_happens: "What happens when you disconnect:",
  gmail_disable_detail: "We will delete your encrypted Gmail refresh token from our servers instantly and permanently. JOBolar will immediately stop sending emails on your behalf and will not be able to send emails until you reconnect Gmail.",
  gmail_disabling: "Disabling...",
  gmail_connected_msg: "Gmail connected successfully!",
  gmail_sign_in_first: "Please sign in first.",
  gmail_consent_saved: "Gmail consent saved.",
  gmail_connect_failed: "Failed to connect to Google. Please try again.",
  gmail_disabled_full: "Gmail sending disabled successfully. Your encrypted token has been removed.",
  gmail_could_not_save: "Could not save consent.",
  gmail_could_not_disable: "Failed to disable Gmail sending.",
  gmail_unable_load: "Unable to load Gmail consent settings.",
  // Preferences Tab (extended)
  pref_heading: "Where Email Auto Apply works today",
  pref_phase1_desc: "Email Auto Apply is available for eligible postings with a valid public employer or recruiter email in supported countries.",
  pref_no_login: "Email Auto Apply does not use job-portal logins. The separate Chrome extension works inside your browser to fill and submit supported forms.",
  pref_future_phases: "Want to understand every available tool? Explore the",
  pref_future_link: "current Jobolar products",
  pref_choose_continent: "1. Choose your target continent",
  pref_phase1_coverage: "Email Auto Apply coverage:",
  pref_countries_across: "countries across",
  pref_continents: "continents.",
  pref_search_optional: "Search countries (optional)",
  pref_search_in: "Search countries in",
  pref_countries_in: "2. Countries supported in",
  pref_apply_note: "Email Auto Apply applies only when a valid public employer or recruiter email is available. Other supported opportunities can be completed with the Chrome extension or manually.",
  pref_no_matches: "No matches.",
  pref_transparency: "Transparency (without exposing sources)",
  pref_transparency_desc: "Email Auto Apply handles eligible email postings; the Chrome extension separately supports browser form filling and submission. Specific discovery sources and automation logic remain private.",
  pref_new_country: "Want us to support a new country? Email",
  pref_work_auth: "3. Work authorization & preferences",
  pref_target_states: "States or provinces to target (if any)",
  pref_press_enter: "Press Enter after each item to add it as a tag.",
  pref_eg_state: "e.g., California",
  pref_add_state: "Add another state",
  pref_exclude_states: "States or provinces to exclude (if any)",
  pref_eg_exclude: "e.g., Quebec",
  pref_authorized: "Are you currently authorized to work in this country?",
  pref_sponsorship: "Will you now or in the future require sponsorship to work in this country?",
  pref_work_pref: "Work preference",
  pref_open: "Open to both remote and in-person",
  pref_remote: "Remote only",
  pref_onsite: "In-person only",
  pref_companies: "Preferred companies (if any)",
  pref_eg_company: "e.g., Google",
  pref_add_company: "Add another company",
  pref_avoid: "Companies to avoid (if any)",
  pref_eg_avoid: "e.g., Acme Corp",
  pref_languages: "Languages you can speak (if any)",
  pref_eg_lang: "e.g., English",
  pref_add_lang: "Add another language",
  pref_min_salary: "Minimum salary",
  pref_selected: "Selected:",
  pref_completion: "Preferences Completion",
  pref_completion_based: "Based on: Selecting at least one country.",
  pref_footer: "Email Auto Apply coverage and posting eligibility are subject to change.",
  pref_was_added: "was added.",
  // Automation Tab
  automation_title: "Automation",
  automation_loading: "Loading",
  automation_daily_email: "Enable daily email",
  // UnsavedChangesModal
  unsaved_title: "Unsaved Changes",
  unsaved_message: "You have unsaved changes. What would you like to do?",
  unsaved_save: "Save & Continue",
  unsaved_discard: "Discard Changes",
  // BillingTab
  billing_title: "Billing & Subscription",
  billing_desc: "Manage your subscription plan and billing information",
  billing_current_plan: "Current Plan",
  billing_active: "Active",
  billing_canceled: "Canceled",
  billing_status: "Status: ",
  billing_next_date: "Next Billing Date",
  billing_cancel: "Cancel Subscription",
  billing_cancel_info: "You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.",
  billing_upgrade: "Upgrade Your Plan",
  billing_upgrade_desc: "Get access to premium features and unlimited job applications.",
  billing_view_pricing: "View Pricing",
  billing_change_plan: "Need to change your plan?",
  billing_change_desc: "Visit our pricing page to upgrade or downgrade your subscription.",
  billing_view_all: "View All Plans",
  billing_cancel_confirm: "Cancel Subscription?",
  billing_cancel_confirm_desc: "Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period.",
  billing_access_until: "Access continues until: ",
  billing_keep: "Keep Subscription",
  billing_yes_cancel: "Yes, Cancel",
  billing_canceling: "Canceling...",
  // ResumeProfilesTab
  resume_primary: "Primary Resume Profile",
  resume_profile_n: "Resume Profile",
  resume_remove_confirm: "Remove this resume profile?",
  resume_remove_failed: "Failed to remove resume: ",
  resume_press_enter: "Press Enter after each title to add it as a tag.",
  resume_lang_label: "Resume Language",
  resume_lang_helper: "Please select language your resume was written",
  resume_lang_en: "English",
  resume_lang_fr: "Français",
  resume_lang_de: "Deutsch",
  resume_lang_es: "Español",
  resume_eg_title: "e.g., Frontend Engineer",
  resume_add_title: "Add another title",
  resume_url_http: "URL must start with http:// or https://",
  resume_url_protocol: "URL must use HTTP or HTTPS protocol",
  resume_url_invalid: "Invalid URL format",
  resume_pdf_docx_only: "Only PDF and DOCX files are allowed.",
  resume_size_limit: "File size exceeds 10MB limit. Please choose a smaller file.",
  resume_no_permission: "You don't have permission to upload files. Please sign in again.",
  resume_auth_required: "Authentication required. Please sign in to upload files.",
  resume_upload_canceled: "Upload was canceled.",
  resume_quota_exceeded: "Storage quota exceeded. Please contact support.",
  resume_validation_failed: "File validation failed. Please try uploading again.",
  resume_upload_retries: "Upload failed after multiple retries. Please check your connection and try again.",
  resume_invalid_url: "Invalid storage URL. Please try again or contact support.",
  resume_upload_failed: "Resume upload failed: ",
  resume_upload_needs_attention: "We couldn’t use that file",
  resume_unreadable_recovery: "We couldn’t read enough text from this résumé. The file may be scanned, password-protected, or damaged. Re-export it as a searchable PDF, or upload the original Word document (.docx), then try again. Your current résumé profile was not changed.",
  resume_account_changed: "Your signed-in account changed. Refresh the page, then try the upload again. Your current résumé profile was not changed.",
  resume_finalize_failed: "We couldn’t finish uploading this résumé. Please check your connection and try again. Your current résumé profile was not changed.",
  resume_choose_another_file: "Choose another file",
  resume_no_file: "No file selected",
  resume_remove_file_confirm: "Remove uploaded resume file?",
  resume_delete_failed: "Failed to delete file",
  resume_preview_failed: "Failed to open resume preview. This can happen when the stored download link is stale or App Check is enforced. Try re-uploading the file or contact support.",
  resume_no_preview: "No storage path or file URL available for preview.",
  resume_signature: "Signature resumes",
  resume_curate: "Curate profiles that feel bespoke",
  resume_build_up: "Build up to {max} polished resume profiles. We'll use these across automations, tailoring, and concierge workflows.",
  resume_slots_remaining: "{count} slots remaining",
  resume_max_slots: "Max slots reached",
  resume_open: "Open",
  resume_selected: "Selected",
  resume_primary_label: "Primary",
  resume_add_profile: "Add profile",
  resume_remove_profile: "Remove Profile",
  resume_limit_reached: "Profile limit reached",
  resume_profile_name: "Profile name",
  resume_profile_name_placeholder: "Resume profile name",
  resume_profile_name_helper: "This is the visible name for this resume profile.",
  resume_target_titles: "Target job titles",
  resume_public_link: "Public reference link",
  resume_upload_label: "Upload resume (PDF or DOCX)",
  resume_link_placeholder: "https://drive.google.com/... (public)",
  resume_anyone_link: "Anyone with the link",
  resume_drop_file: "Drop file here or click to upload",
  resume_accepted: "Accepted: PDF, DOCX only \u2022 Max 10MB",
  resume_uploading: "Uploading...",
  resume_remove_file: "Remove File",
  resume_preview: "Resume preview",
  resume_pdf_no_preview: "PDF preview not supported.",
  resume_docx_saved: "Preview not available for .docx. File saved.",
  resume_pro_tip: "Pro tip: Keep one profile per job family (e.g., Frontend, Data, PM) for snappier tailoring.",
  resume_completeness: "Resume Completeness",
  resume_completeness_based: "Based on: Target job titles + (Public link OR uploaded resume).",
  resume_saved: "Resume profiles saved successfully.",
  resume_save_error: "Failed to save resume profiles. Please try again.",
  resume_no_titles: "No Target Job Titles",
  resume_no_titles_desc: "You have not added any target job titles. Adding titles helps us better match your resume to relevant opportunities.",
  resume_no_titles_question: "Do you want to continue without target titles, or go back to add them?",
  resume_go_back: "Go Back & Add Titles",
  resume_continue_without: "Continue Without Titles",
  resume_enter_link: "Please enter a public resume link.",
  resume_upload_file: "Please upload a resume file (PDF or DOCX).",
  resume_title_added: "was added as a target title.",
  // UploadCertificatesTab
  cert_primary: "Primary Certificate",
  cert_n: "Certificate",
  cert_load_failed: "Failed to load certificate files",
  cert_save_failed: "Failed to save certificate files",
  cert_pdf_only: "Only PDF files are allowed for certificates.",
  cert_size_limit: "File size exceeds 6MB limit. Please choose a smaller file.",
  cert_total_limit: "Total certificate files size would exceed 30MB limit.",
  cert_invalid_slot: "Invalid slot",
  cert_no_permission: "You don't have permission to upload files. Please sign in again.",
  cert_auth_required: "Authentication required. Please sign in to upload files.",
  cert_upload_canceled: "Upload was canceled.",
  cert_quota_exceeded: "Storage quota exceeded. Please contact support.",
  cert_upload_failed: "Certificate upload failed: ",
  cert_no_file: "No file selected",
  cert_remove_confirm: "Remove this certificate?",
  cert_delete_failed: "Failed to delete file",
  cert_deletion_failed: "Certificate deletion failed:",
  cert_remove_failed: "Failed to remove certificate: ",
  cert_preview_failed: "Failed to open certificate preview. Try re-uploading the file or contact support.",
  cert_no_preview: "No storage path or file URL available for preview.",
  cert_heading: "Upload Certificates",
  cert_subheading: "Showcase your credentials",
  cert_desc: "Upload up to {max} certificate files (PDF only). Maximum 6 MB per file, 30 MB total.",
  cert_combine_note: "Note: You can also combine all your certificates into a single PDF and upload it as one file using",
  cert_combine_site: "online2pdf.com",
  cert_combine_suffix: "instead of providing them one by one.",
  cert_slots_remaining: "{count} slots remaining",
  cert_max_slots: "Max slots reached",
  cert_open: "Open",
  cert_selected: "Selected",
  cert_primary_label: "Primary",
  cert_add: "Add certificate",
  cert_remove: "Remove Certificate",
  cert_limit_reached: "Certificate limit reached",
  cert_slot: "Certificate slot",
  cert_slot_name: "Certificate slot name",
  cert_slot_helper: "This is the visible name for this certificate slot.",
  cert_label: "Certificate Label (Name)",
  cert_label_placeholder: "e.g., AWS Solutions Architect, PMP, Google Cloud",
  cert_label_helper: "A short descriptive name for this certificate.",
  cert_input_method: "Certificate Input (choose one method)",
  cert_public_link: "Public reference link",
  cert_upload_label: "Upload certificate (PDF)",
  cert_link_placeholder: "https://drive.google.com/... (public)",
  cert_anyone_link: "Anyone with the link",
  cert_drop_file: "Drop file here or click to upload",
  cert_accepted: "Accepted: PDF only \u2022 Max 6MB per file",
  cert_uploading: "Uploading...",
  cert_remove_file: "Remove File",
  cert_pdf_no_preview: "PDF preview not supported.",
  cert_pro_tip: "Pro tip: Keep each slot for a specific certification type or combine them all in a single PDF.",
  cert_completeness: "Certificate Completeness",
  cert_completeness_based: "Based on: Certificate label + (Public link OR uploaded file).",
  cert_saved: "Certificate files saved successfully.",
  cert_save_error: "Failed to save certificate files. Please try again.",
  // AiBuddyPanel
  buddy_name: "AI Chatty Buddy",
  buddy_subtitle: "Your AI guide for onboarding",
  buddy_live: "Live",
  buddy_prompt1: "Review this attachment for a Data Analyst role.",
  buddy_prompt2: "Give me 5 prioritized edits for impact.",
  buddy_prompt3: "Score my resume (A\u2013F) with 3 reasons.",
  buddy_placeholder: "Ask AI Chatty Buddy for ideas, feedback, or next steps\u2026",
  buddy_thinking: "AI Chatty Buddy is thinking\u2026",
  buddy_attaching: "Attaching resume...",
  buddy_attach_resume: "Attach this resume",
  buddy_attach_desc: "Attach to include resume content in your conversation",
  buddy_will_attach: "Will attach: ",
  buddy_one_time: "(one-time)",
  buddy_remove_file: "Remove attached file",
  buddy_attach_file: "Attach file",
  buddy_prompts_used: "prompts used",
  buddy_limit_reached: "You have used all {max} prompts. Please wait until your cooldown ends.",
  buddy_daily_limit: "AI prompt allowance exhausted.",
  buddy_unreachable: "Unable to reach the assistant.",
  buddy_no_response: "I'm here, but I didn't receive any text back from the model.",
  buddy_connection_error: "I'm having trouble connecting to the AI service right now. Please try again in a moment.",
  country_no_matches: "No matches found",
  country_type_to_search: "Type to search",
  toast_load_error: "Unable to load saved onboarding data.",
  toast_init_error: "Unable to initialise your profile.",
  toast_save_success: "Changes saved successfully.",
  toast_save_error: "Failed to save changes. Please try again.",
  toast_signin_upload: "Please sign in to upload resumes.",
  toast_slot_error: "Failed to update resume slot.",
  toast_upload_success: "Resume uploaded successfully.",
  toast_upload_error: "Resume upload failed. Please try again.",
  ai_confirm_preview: "Please confirm the resume preview before proceeding.",
  ai_upload_resume: "Please upload your resume before proceeding.",
  ai_no_text: "No resume text extracted. Please upload a valid resume.",
  ai_enter_jd: "Please enter the job description.",
  ai_success: "AI generation successful!",
  ai_failed: "AI generation failed. Please try again.",
  ai_error: "Error generating resume. Please try again.",
  ai_build_btn: "Build with AI",
},

// ─── Product Pages ─────────────────────────────────────
product: {
  // Tabs & shared
  tab_auto_apply: "Auto-Apply",
  tab_cover_letter: "Cover Letter AI",
  tab_resume: "Resume Builder",
  tab_smart_apply: "Smart Apply Assist",
  tab_future: "Current Capabilities",
  product_label: "Product",
  product_features_label: "JOBolar product features",
  see_how: "See How It Works",
  gmail_oauth: "Gmail only (OAuth)",
  disclaimer_see: "See",
  disclaimer_privacy: "Privacy",
  disclaimer_terms: "Terms",
  disclaimer_refund: "Refund Policy",
  // Auto Apply
  aa_title: "Auto Apply — Email-Powered Job Applications",
  aa_desc: "Schedule applications for eligible postings that provide a valid public employer or recruiter email. JOBolar sends from your connected Gmail using the resume mode and AI cover letter included with your plan.",
  aa_get_started: "Get Started",
  aa_no_passwords: "No passwords stored",
  aa_pause_caps: "Pause / daily caps",
  aa_why: "Why Auto Apply?",
  aa_b1_title: "Apply In Minutes",
  aa_b1_desc: "For eligible email postings, let JOBolar send your resume, AI cover letter, and selected certificates from your connected Gmail.",
  aa_b2_title: "Local & Abroad",
  aa_b2_desc: "Apply to jobs in your home country or overseas from one dashboard. Switch targets anytime.",
  aa_b3_title: "Privacy-First",
  aa_b3_desc: "OAuth only (no passwords). Minimal Gmail scopes and encryption in transit/at rest.",
  aa_b4_title: "Daily Summaries",
  aa_b4_desc: "Get a concise email recap of roles applied, links to employers, and any replies received.",
  aa_how: "How Auto Apply Works",
  aa_s1_title: "Connect Gmail",
  aa_s1_desc: "Sign in with Google (Gmail only). We recommend a new, professional Gmail for job search. You can revoke access anytime.",
  aa_s2_title: "Set Targets",
  aa_s2_desc: "Pick roles, locations (local or abroad), and boards. Choose your daily send limit to protect deliverability.",
  aa_s3_title: "Send & Track",
  aa_s3_desc: "JOBolar emails applications from your Gmail. Track progress in your dashboard and get daily summaries.",
  aa_start_applying: "Start Applying Now",
  aa_quality_title: "Built-in Quality, Less Busywork",
  aa_h1: "Email Auto Apply runs only for eligible postings with a valid public employer or recruiter email.",
  aa_h2: "AI Chatty Buddy can draft or refine resumes & cover letters.",
  aa_h3: "Deliverability best-practices and pacing built-in.",
  aa_h4: "Clear controls: pause, resume, and daily caps.",
  aa_disclaimer: "Email Auto Apply sends eligible applications through Gmail with your explicit permission and never stores your Gmail password. It does not fill browser forms; the separate Jobolar Chrome extension can fill and submit supported forms with user controls. Email delivery and employer responses are not guaranteed.",
  // Cover Letter AI
  cl_title: "Cover Letter AI",
  cl_desc: "Generate tailored cover letters in minutes with AI Chatty Buddy. Choose tone and length, edit freely, then attach to your Gmail\u2011based application — for roles at home or abroad.",
  cl_start_writing: "Start Writing Now",
  cl_review_send: "Review before send",
  cl_tone_controls: "Tone & length controls",
  cl_why: "Why Cover Letter AI?",
  cl_b1_title: "Personalized For Every Role",
  cl_b1_desc: "AI Chatty Buddy drafts tailored cover letters using the job description and your resume highlights.",
  cl_b2_title: "Adjust Tone & Length",
  cl_b2_desc: "Choose concise, standard, or detailed. Switch tone to professional, friendly, or formal in one click.",
  cl_b3_title: "You Approve Before Sending",
  cl_b3_desc: "Edit inline, track changes, and keep full control. Nothing goes out without your review.",
  cl_b4_title: "Local Or Abroad",
  cl_b4_desc: "Apply in your country or internationally—templates adapt to region and employer expectations.",
  cl_how: "How Cover Letter AI Works",
  cl_s1_title: "Provide The Basics",
  cl_s1_desc: "Paste a job description or import a link. We use your saved resume profile as context.",
  cl_s2_title: "Tailor With AI Chatty Buddy",
  cl_s2_desc: "Generate a draft in seconds. Adjust tone, add achievements, and include role\u2011specific keywords.",
  cl_s3_title: "Approve & Send",
  cl_s3_desc: "Attach your final cover letter to the email application sent from your Gmail via Auto Apply.",
  cl_generate: "Generate My Cover Letter",
  cl_what_you_get: "What You\u2019ll Get",
  cl_h1: "ATS\u2011aware phrasing and clean formatting.",
  cl_h2: "Company\u2011aware hooks (role, team, product) from the job post.",
  cl_h3: "One\u2011click variants: concise, standard, detailed.",
  cl_h4: "Works with Auto Apply — all via Gmail (OAuth).",
  cl_disclaimer: "Cover Letter AI generates drafts you control. Depending on your plan and workflow, a letter can be used for an eligible Email Auto Apply submission or a supported Chrome extension application. Review AI-generated content for accuracy before using it.",
  // Resume Builder
  rb_title: "Resume Builder",
  rb_desc: "Build a polished, ATS\u2011friendly resume with AI Chatty Buddy — create from scratch or refine your existing document, then use it directly with Auto Apply.",
  rb_build: "Build My Resume",
  rb_privacy_first: "Privacy\u2011first",
  rb_export_pdf: "Export to PDF",
  rb_why: "Why Resume Builder?",
  rb_b1_title: "AI\u2011Assisted Drafting",
  rb_b1_desc: "Use AI Chatty Buddy to create a professional resume from scratch or improve what you already have.",
  rb_b2_title: "Clean, ATS\u2011Friendly Layouts",
  rb_b2_desc: "Pick from streamlined layouts designed to pass automated screenings without heavy graphics.",
  rb_b3_title: "Import & Parse",
  rb_b3_desc: "Upload a PDF/Doc to extract experience and skills, then review and edit before saving.",
  rb_b4_title: "Multiple Versions",
  rb_b4_desc: "Keep role\u2011specific variants and switch the version you send per job.",
  rb_how: "How Resume Builder Works",
  rb_s1_title: "Start With Your Details",
  rb_s1_desc: "Enter your experience, education, and skills — or let AI Chatty Buddy guide the process with prompts.",
  rb_s2_title: "Choose a Layout",
  rb_s2_desc: "Select an ATS\u2011friendly template. Tweak sections, keywords, and highlights for each role.",
  rb_s3_title: "Save & Use In Applications",
  rb_s3_desc: "Save your resume and pick it when applying. Create versions for different roles or markets.",
  rb_try: "Try Resume Builder",
  rb_what_you_love: "What You\u2019ll Love",
  rb_h1: "AI suggestions you can accept, edit, or ignore.",
  rb_h2: "Export to PDF for sharing; use directly with Auto Apply.",
  rb_h3: "Version control for different roles (e.g., Data Analyst vs. Business Analyst).",
  rb_h4: "Privacy\u2011first: your content stays in your account.",
  rb_disclaimer: "Resume Builder provides AI\u2011assisted drafting you control. Review every change before saving or using a resume with eligible Email Auto Apply or a supported Chrome extension application.",
  // Smart Apply Assist
  sa_title: "Smart Apply Assist",
  sa_desc: "Find better\u2011fit roles faster. Smart Apply Assist scores jobs against your profile and targets, so you spend time where it counts — locally or abroad — then send polished applications via Gmail.",
  sa_get_matched: "Get Matched Faster",
  sa_local_abroad: "Local & abroad",
  sa_why: "Why Smart Apply Assist?",
  sa_b1_title: "Relevant Matches",
  sa_b1_desc: "We surface roles that align with your skills, visa preferences, and target countries.",
  sa_b2_title: "Advanced Filters",
  sa_b2_desc: "Fine\u2011tune by role, location (local or abroad), seniority, and keywords to keep noise low.",
  sa_b3_title: "Score & Prioritize",
  sa_b3_desc: "See a match score and focus on high\u2011fit jobs first for better conversion.",
  sa_b4_title: "AI Chatty Buddy Assist",
  sa_b4_desc: "Generate or tailor resumes and cover letters for each role before sending via Gmail.",
  sa_how: "How Smart Apply Assist Works",
  sa_s1_title: "Set Targets",
  sa_s1_desc: "Choose your roles, countries, and keywords. Tell us if you prefer local or international opportunities.",
  sa_s2_title: "Get Smart Matches",
  sa_s2_desc: "We scan sources and bring you a focused list with match scores and why\u2011it\u2011fits highlights.",
  sa_s3_title: "Apply Faster",
  sa_s3_desc: "Use AI Chatty Buddy to tailor documents, then send applications by Gmail with a click — track status daily.",
  sa_start_matching: "Start Matching Now",
  sa_what_sets_apart: "What Sets It Apart",
  sa_h1: "Continue through eligible Email Auto Apply, a supported Chrome extension form, or a manual application link.",
  sa_h2: "Region\u2011aware targeting: your country or abroad.",
  sa_h3: "Daily caps and pause/resume controls to protect deliverability.",
  sa_h4: "Explainable matching — see keywords and gaps before you apply.",
  sa_disclaimer: "Jobolar's matching tools help you identify relevant roles and prepare documents. Email Auto Apply is limited to eligible postings with a valid public email; the Chrome extension can fill and submit supported browser forms. Job availability, delivery, and employer responses are not guaranteed.",
  // Current capabilities (legacy key names retained for compatible links)
  fp_badge: "Product \u00b7 Available now",
  fp_title: "Current Jobolar Products",
  fp_desc: "Explore Jobolar's live job search, email automation, Chrome extension, resume and cover-letter tools, and Interview Assistant.",
  fp_timeline_note: "Feature availability depends on your plan, device, country, job posting, and supported website.",
  fp_p1_label: "Product 1 \u00b7 Live",
  fp_p1_title: "Scheduled Email Auto Apply",
  fp_p1_target: "Live now",
  fp_p1_desc: "Jobolar schedules email applications only for eligible postings with a valid public employer or recruiter email in a supported country.",
  fp_p1_b1: "Uses your connected Gmail with explicit permission",
  fp_p1_b2: "Uses a Base Resume or eligible AI-tailored resume based on your plan",
  fp_p1_b3: "Alerts you when a browser or manual application is required",
  fp_p1_highlight: "Posting eligibility is separate from country support: every job still needs a valid public application email.",
  fp_coverage_title: "Email Auto Apply country coverage",
  fp_coverage_desc: "Click a continent to view supported countries. Jobs without a valid public employer or recruiter email require the Chrome extension or a manual application.",
  fp_countries_in: "Countries in {name}",
  fp_africa: "Africa",
  fp_europe: "Europe",
  fp_north_america: "North America",
  fp_p2_label: "Product 2 \u00b7 Live",
  fp_p2_title: "Jobolar Chrome Extension",
  fp_p2_target: "Available now",
  fp_p2_desc: "Use Copilot Fill, Smart Form, saved answers, AI answer assistance, Smart Apply, and scheduled Chrome Auto Apply on supported job websites.",
  fp_p2_b1: "Deterministic Copilot Fill is available to Basic users",
  fp_p2_b2: "Standard and Pro unlock Smart Apply and automation features",
  fp_p2_b3: "Review, pause, resume, cancel, and supported automatic submission controls",
  fp_p2_highlight: "The extension works inside your browser and follows the controls and availability shown for each supported site.",
  fp_p3_label: "Product 3 \u00b7 Live",
  fp_p3_title: "AI Job Search & Matching",
  fp_p3_target: "Available now",
  fp_p3_desc: "Receive daily local and international matches based on your profile, preferences, and target roles.",
  fp_p3_b1: "Curated local and international opportunities",
  fp_p3_b2: "Direct job links and unlimited alerts",
  fp_p3_b3: "Job-search and application analytics",
  fp_p3_highlight: "Matching is included across the main Jobolar subscription plans.",
  fp_p4_label: "Product 4 \u00b7 Live",
  fp_p4_title: "AI R\u00e9sum\u00e9 Tailoring & Cover Letters",
  fp_p4_target: "Available now",
  fp_p4_desc: "Use Base Resume, Smart Tailor, or Deep Tailor where your plan allows, together with job-specific AI cover letters.",
  fp_p4_b1: "Base Resume keeps your content unchanged",
  fp_p4_b2: "Smart Tailor uses information already in your resume",
  fp_p4_b3: "Deep Tailor also uses your completed and verified Career Profile",
  fp_p4_highlight: "Review AI-generated application materials for accuracy before using them.",
  fp_status_live: "Live",
  fp_status_progress: "In progress",
  fp_status_planned: "Planned",
  fp_status_concept: "Concept",
  // Product Buddy
  pb_title: "AI Product Buddy",
  pb_subtitle: "Your product documentation assistant",
  pb_live: "Live",
  pb_initial_msg: "Hi there! I\u2019m your AI Product Buddy. Ask me about Job Search & Matching, Email Auto Apply, the Chrome extension, r\u00e9sum\u00e9 tailoring and cover letters, or Interview Assistant. I\u2019ll only answer using the product docs.",
  pb_viewing_tab: "You\u2019re viewing {tabName}. Ask anything about this feature and I\u2019ll quote the matching section.",
  pb_no_index: "Indexing not available yet for this document.",
  pb_not_found: "I couldn\u2019t find that in the Product docs. Try asking about Job Search & Matching, Email Auto Apply, the Chrome extension, r\u00e9sum\u00e9 tailoring and cover letters, or Interview Assistant.",
  pb_sq1: "How does Auto-Apply work?",
  pb_sq2: "What makes Smart Apply Assist helpful?",
  pb_sq3: "Does Resume Builder mention templates?",
  pb_sq4: "What can Cover Letter AI generate?",
  pb_sq5: "Which Jobolar products are available now?",
  pb_ask_about: "Ask about {tabName}\u2026",
  pb_searching: "Searching product documentation\u2026",
},

// ─── Legal Pages ───────────────────────────────────────
legal: {
  tab_terms: "Terms of Service",
  tab_privacy: "Privacy Policy",
  tab_refund: "Refund Policy",
  last_updated: "Last Updated",
  legal_docs_label: "JOBolar legal documents",
  // AI Legal Buddy
  lb_title: "AI Legal Buddy",
  lb_desc: "Your legal document assistant",
  lb_live: "Live",
  lb_initial_msg: "Hi there! I\u2019m your JOBolar legal assistant. Ask me about our Terms of Service, Privacy Policy, or Refund Policy.",
  lb_viewing_tab: "You are viewing the {tabName} document. Ask anything about our policies, and I\u2019ll cite the exact section for you.",
  lb_no_index: "I couldn\u2019t find that in our legal documents.",
  lb_not_found: "I couldn\u2019t find that in our legal documents. Try asking about privacy, terms of service, or refund policy.",
  lb_sq1: "What does the refund window cover?",
  lb_sq2: "How does JOBolar handle my data?",
  lb_sq3: "When can JOBolar suspend access?",
  lb_ask_about: "Ask about the {tabName}\u2026",
  lb_searching: "Searching legal documents\u2026",

  // ─── Terms of Service ──────────────────────────────
  terms_title: "Terms of Service",
  terms_last_updated_date: "August 29, 2026",
  terms_intro: "These Terms of Service (\"Terms\") govern your access to and use of the web application, products, and services provided by JOBolar.Ai (\"Jobolar\", \"we\", \"us\"). By creating an account or using the Services, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Services.",
  terms_disclaimer: "This document is for informational purposes and does not constitute legal advice. Local laws may grant additional rights that prevail over conflicting provisions.",
  terms_s1_title: "1) Acceptance & Eligibility",
  terms_s1_li1: "You must be at least the age of majority in your jurisdiction (and in all cases at least 16) to use the Services.",
  terms_s1_li2: "By using the Services on behalf of an organization, you represent that you have authority to bind that organization to these Terms.",
  terms_s1_li3: "We may update these Terms from time to time. Material changes will be communicated via the dashboard or email. Continued use constitutes acceptance of the revised Terms.",
  terms_s2_title: "2) Account, Gmail Requirement & Security",
  terms_s2_li1: "Registration is via Google Sign\u2011In. Gmail is required to enable sending applications on your behalf.",
  terms_s2_li2: "We recommend using a new, professional Gmail account reserved for job applications (e.g., firstname.lastname@gmail.com).",
  terms_s2_li3: "You are responsible for safeguarding your credentials and for all activity that occurs under your account.",
  terms_s2_li4: "You may revoke Google OAuth access at any time. Revocation may disable key features of the Services.",
  terms_s2_gmail_title: "Gmail Sending Permission",
  terms_s2_gmail_li1: "By enabling Gmail sending, you authorize JOBolar to send job-application emails from your Gmail account via the Gmail API on your behalf.",
  terms_s2_gmail_li2: "We store an encrypted refresh token on our servers to enable this functionality. The token is used exclusively to send emails you trigger through the Services.",
  terms_s2_gmail_li3: "The encrypted token will be deleted instantly and permanently when you disconnect Gmail via the JOBolar dashboard or revoke access through your Google Account permissions.",
  terms_s2_gmail_li4: "Revoking access halts email sending immediately. You will need to reconnect Gmail to resume using the email-sending features.",
  terms_s2_prohibited_title: "Prohibited Uses of Gmail Sending",
  terms_s2_prohibited_li1: "You must not use JOBolar to send spam, unsolicited bulk messages, or any content that violates Gmail's policies or applicable laws.",
  terms_s2_prohibited_li2: "You must comply with Gmail's anti-spam rules and all relevant email-communication regulations.",
  terms_s2_prohibited_li3: "JOBolar always respects Gmail's daily sending limits (typically 500 emails/day for standard Gmail accounts). Even if a higher number is configured, JOBolar will never exceed Google's allowed sending limits.",
  terms_s2_prohibited_li4: "Because all messages are sent from your Gmail address, you are responsible for ensuring that your use of JOBolar complies with employment, privacy, and anti-spam laws in both your jurisdiction and the recipient's jurisdiction.",
  terms_s3_title: "3) Description of Services",
  terms_s3_li1: "JOBolar automates email\u2011based job applications from your Gmail, attaching your resume, cover letter, and certificates as directed.",
  terms_s3_li2: "Email Auto Apply does not log in to third\u2011party job portals and works only for eligible postings with a valid public application email. The separate Jobolar Chrome extension uses Copilot Fill and Smart Apply to autofill compatible fields and, where supported and authorized, submit job-application forms within the user\u2019s active browser session and subject to the controls shown there.",
  terms_s3_li3: "AI Chatty Buddy (JOBolar AI) can generate and tailor application materials using the information you provide. You are responsible for reviewing your resume content to ensure accuracy and suitability before enabling JOBolar to send applications on your behalf.",
  terms_s3_li4: "Availability of third\u2011party boards, deliverability, and outcomes are not guaranteed and may vary by region and employer systems.",
  terms_s4_title: "4) User Responsibilities & Acceptable Use",
  terms_s4_li1: "Provide accurate information and use a professional tone in communications with employers.",
  terms_s4_li2: "Do not use the Services to send spam, unlawful, misleading, or harassing messages.",
  terms_s4_li3: "Respect rate limits and deliverability guidance; do not attempt to circumvent technical restrictions.",
  terms_s4_li4: "Comply with applicable laws, including anti\u2011spam, privacy, and employment laws in your jurisdiction and the recipient's jurisdiction.",
  terms_s4_li5: "You are solely responsible for the content of your applications and for determining whether a role is appropriate for you.",
  terms_s5_title: "5) Subscriptions, Billing & Refunds",
  terms_s5_intro: "Paid plans renew automatically unless cancelled prior to renewal. Billing is handled by our payment processors. By subscribing you authorize recurring charges.",
  terms_s5_li1: "Refunds are governed by our Refund Policy.",
  terms_s5_li2: "Taxes, currency conversion, and bank fees are your responsibility.",
  terms_s5_li3: "Downgrades take effect on the next renewal date; no partial refunds for unused time unless required by law.",
  terms_s5a_title: "5A) Payment Processors",
  terms_s5a_intro: "JOBolar uses trusted third-party payment processors to securely handle all transactions:",
  terms_s5a_stripe: "Stripe: used for global debit and credit card payments.",
  terms_s5a_flutterwave: "Flutterwave: used for bank transfer and mobile money payments in supported regions (primarily Africa).",
  terms_s5a_authorize: "By subscribing, you authorize JOBolar and its payment partners to process payments using your selected method.",
  terms_s5a_no_store: "JOBolar does not store full payment credentials such as card numbers or bank account details. All sensitive payment information is securely processed by our payment providers in accordance with their security and compliance standards.",
  terms_s5a_subject: "Payment processing, authorization, and settlement may be subject to the terms and policies of Stripe and Flutterwave, respectively.",
  terms_s6_title: "6) Intellectual Property",
  terms_s6_li1: "The Services, including software, branding, and content, are owned by JOBolar and its licensors and are protected by IP laws.",
  terms_s6_li2: "Subject to your compliance with these Terms, we grant you a limited, non\u2011exclusive, non\u2011transferable license to use the Services.",
  terms_s7_title: "7) User Content & License to JOBolar",
  terms_s7_li1: "You retain ownership of documents and materials you upload or generate, including resumes, cover letters, and certificates.",
  terms_s7_li2: "You grant JOBolar a worldwide, non\u2011exclusive license to host, process, and transmit your content solely to provide the Services.",
  terms_s7_li3: "Your uploaded resumes and cover letters remain your property and are used only for the requested job-application service.",
  terms_s7_li4: "You represent that you have all rights necessary to grant this license and to send the content to employers.",
  terms_s8_title: "8) Deliverability, Limits & Third\u2011Party Systems",
  terms_s8_li1: "Email deliverability, spam filtering, and employer response timelines are outside our control.",
  terms_s8_li2: "We may pace or cap sending to protect deliverability and comply with provider guidelines.",
  terms_s8_li3: "Third\u2011party references (e.g., job boards) are for identification only; no endorsement or affiliation is implied unless stated.",
  terms_s9_title: "9) Third\u2011Party Links & Services",
  terms_s9_li1: "The Services may link to third\u2011party websites or services. We are not responsible for their content, policies, or practices.",
  terms_s9_li2: "Your use of third\u2011party services is governed by their terms and privacy policies.",
  terms_s10_title: "10) Disclaimers",
  terms_s10_text: "THE SERVICES ARE PROVIDED \u201CAS IS\u201D AND \u201CAS AVAILABLE.\u201D TO THE MAXIMUM EXTENT PERMITTED BY LAW, JOBolar DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.",
  terms_s11_title: "11) Limitation of Liability",
  terms_s11_text: "TO THE FULLEST EXTENT PERMITTED BY LAW, JOBolar AND ITS SUPPLIERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES.",
  terms_s12_title: "12) Indemnification",
  terms_s12_text: "You agree to defend, indemnify, and hold harmless JOBolar, its affiliates, and their officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising from your use of the Services or violation of these Terms.",
  terms_s13_title: "13) Termination & Suspension",
  terms_s13_li1: "You may stop using the Services at any time; cancelling a paid plan prevents future renewals as described in the Refund Policy.",
  terms_s13_li2: "We may suspend or terminate access if you violate these Terms, create security risks, or engage in fraud/abuse.",
  terms_s13_li3: "Upon termination, rights granted to you under these Terms will cease immediately.",
  terms_s14_title: "14) Governing Law & Dispute Resolution",
  terms_s14_text: "These Terms are governed by the applicable laws of the jurisdiction in which JOBolar operates, without regard to conflict\u2011of\u2011law principles. Where permitted by law, disputes will be resolved through binding arbitration.",
  terms_s15_title: "15) Changes to the Services or Terms",
  terms_s15_li1: "We may modify or discontinue features to improve reliability, security, or compliance.",
  terms_s15_li2: "We will provide notice of material changes to these Terms. Your continued use after changes take effect constitutes acceptance.",
  terms_s16_title: "16) Contact",
  terms_s16_intro: "Questions about these Terms? Contact us at:",
  terms_s16_email_label: "Email:",
  terms_s16_address_label: "Physical Address:",
  terms_s16_support: "You can also visit our support page for additional assistance.",

  // ─── PRIVACY POLICY ──────────────────────────────────
  priv_title: "Privacy Policy",
  priv_updated: "Last Updated:",
  priv_date: "September 3, 2026",
  priv_intro: 'This Privacy Policy explains how {company} ("Jobolar", "we", "us") collects, uses, discloses, and protects your information when you use our services.',
  priv_disclaimer: "This document is for informational purposes and does not constitute legal advice. Rights and obligations may differ by jurisdiction (e.g., GDPR, CCPA).",

  priv_s1_title: "1) Information We Collect",
  priv_s1a_title: "A. Account & Identity",
  priv_s1a_li1: "Primary Google Account email address and public profile info (name, profile photo, preferred language) via Google OAuth.",
  priv_s1a_li2: "Country/region, time zone, and interface language (derived from device/browser settings).",
  priv_s1b_title: "B. Job Search Profile",
  priv_s1b_li1: "Targets (roles, locations—local and abroad, preferred platforms/boards).",
  priv_s1b_li2: "Daily send limits, pause/resume state, notification preferences.",
  priv_s1c_title: "C. Content You Provide",
  priv_s1c_li1: 'Uploaded files (resumes/CVs, certificates - optional).',
  priv_s1c_li2: 'Application materials generated with JOBolar AI ("AI Chatty Buddy").',
  priv_s1d_title: "D. Application & Usage Data",
  priv_s1d_li1: "Application metadata (timestamp, recipient, subject, job title/company, delivery status).",
  priv_s1d_li2: "Device/technical logs (IP, user agent, browser/OS, crash logs) for security and diagnostics.",
  priv_s1e_title: "E. Gmail Integration (Sensitive Scope Use)",
  priv_s1e_text: "The only Gmail credential stored by JOBolar is a securely encrypted refresh token. This token allows sending emails initiated by the user; we do not store your Gmail password and we never read your inbox or access other Gmail data. The token is encrypted at rest and used exclusively for sending user-triggered job-application emails.",
  priv_s1f_title: "F. Non-Collection Notice",
  priv_s1f_text: "We do not collect, read, or access your Gmail inbox, email bodies, subjects, attachments, or any other Gmail message data. We do not ask for or store Gmail passwords.",
  priv_s1g_title: "G. Payment Information",
  priv_s1g_text: "We do not store full payment credentials such as credit card numbers or bank account details. Payments are processed securely by third-party providers:",
  priv_s1g_li1: "Stripe (for card payments)",
  priv_s1g_li2: "Flutterwave (for bank transfer and mobile money payments)",
  priv_s1g_text2: "These providers may collect and process payment-related information in accordance with their own privacy and security policies.",

  priv_s2_title: "2) How We Use Your Information",
  priv_s2_li1: "Send job applications via your Gmail with attachments you upload or generate (resume, cover letter, certificates).",
  priv_s2_li2: "Provide job-matching, dashboards, notifications (daily summaries), and status tracking.",
  priv_s2_li3: "Generate or refine resumes and cover letters using AI Chatty Buddy at your request.",
  priv_s2_li4: "Protect the service through authentication, Firebase App Check, feature-specific usage limits, and infrastructure-level abuse protections.",
  priv_s2_li5: "Improve the Services (quality, reliability, deliverability) using aggregated, de-identified metrics.",
  priv_s2_li6: "Comply with legal obligations and enforce our Terms, including investigating misuse.",
  priv_s2_bases: "Jobolar requests consent where required, including for Google OAuth and optional device permissions. Other processing may be necessary to provide and secure the service or comply with legal obligations.",

  priv_s3_title: "3) Google Permissions & OAuth Scopes",
  priv_s3_intro: "We use Google OAuth to connect your Gmail. Requested scopes are limited to what is necessary to perform email-based applications:",
  priv_s3_scope1: "openid – verify your identity with Google",
  priv_s3_scope2: "email – see your primary Google Account email address, used to identify your account and route notifications",
  priv_s3_scope3: "profile – basic public profile (name, photo, preferred language) for personalization",
  priv_s3_scope4: "https://www.googleapis.com/auth/gmail.send – send email on your behalf for job applications sent from your Gmail address",
  priv_s3_minimal: "We request the minimal scope required and do not request full read access to your inbox contents. The gmail.send scope does not permit reading inbox messages.",
  priv_s3_revoke: "You can revoke access anytime in Google Account → Security → Third‑party access.",
  priv_s3_consent: "Google OAuth connection and Gmail sending occur only after you grant the requested permission. We maintain limited operational records for selected actions.",
  priv_s3_token_title: "Gmail Token Handling",
  priv_s3_token_li1: "We store an encrypted Gmail refresh token on our servers to enable sending emails on your behalf.",
  priv_s3_token_li2: "The token is encrypted at rest using industry-standard encryption.",
  priv_s3_token_li3: "Token usage is limited exclusively to sending user-triggered job-application emails.",
  priv_s3_token_li4: "We do not store your Gmail password at any time.",
  priv_s3_token_li5: "Disconnecting Gmail through Jobolar deletes the stored Gmail credential. Revoking access through Google Account settings prevents further authorized Gmail access.",
  priv_s3_token_li6: "Gmail credentials are retained only while your Gmail connection is active and are deleted when you disconnect Gmail through Jobolar.",
  priv_s3_limited_title: "Limited Use & Google API Compliance",
  priv_s3_limited_intro: "JOBolar's use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.",
  priv_s3_limited_before: "JOBolar's use of information received from Google APIs adheres to the ",
  priv_s3_limited_link: "Google API Services User Data Policy",
  priv_s3_limited_after: ", including the Limited Use requirements.",
  priv_s3_limited_li1: "We do not use Gmail data for advertising purposes.",
  priv_s3_limited_li2: "We do not use Gmail data to train generalized AI models or for any purpose unrelated to providing the job-application service.",
  priv_s3_limited_li3: "We do not permit human access to your Gmail data without your affirmative consent.",
  priv_s3_limited_li4: "Gmail data is used solely to send job-application emails you initiate and approve.",

  priv_s4_title: "4) Data Storage, Retention & Deletion",
  priv_s4_intro: "We use Firebase (Google Cloud) for hosting and storage. Data is encrypted in transit and at rest. Access is role-restricted.",
  priv_s4_retention_title: "Retention",
  priv_s4_retention_li1: "Account data: retained while your account is active.",
  priv_s4_retention_li2: "Application metadata & generated docs: retained for your dashboard history unless you delete them.",
  priv_s4_retention_li3: "Limited residual copies may remain temporarily in security logs, backups, or service-provider systems and are removed according to applicable retention schedules.",
  priv_s4_deletion_title: "Deletion",
  priv_s4_deletion_li1: "Self‑service deletion from dashboard for documents and profile items.",
  priv_s4_deletion_li2: "When account deletion completes, Jobolar immediately removes the user's active account data. Limited residual copies may remain temporarily in security logs, backups, or service-provider systems and are removed according to applicable retention schedules.",
  priv_s4_deletion_li3: "You may also revoke OAuth access at any time from your Google Account.",

  priv_s5_title: "5) AI Processing (AI Chatty Buddy)",
  priv_s5_li1: "Creates/updates resumes and generates job‑specific cover letters using your inputs.",
  priv_s5_li2: "Stores outputs under your account to attach to applications and reuse later.",
  priv_s5_li3: "Jobolar uses DeepSeek and Google's paid Gemini API for selected AI features. DeepSeek has confirmed Jobolar's training opt-out, and Google states that paid Gemini API prompts and responses are not used to improve its products.",
  priv_s5_li4: "AI outputs may require your review for accuracy and compliance with job requirements.",

  priv_s6_title: "6) Sharing & Disclosure",
  priv_s6_li1_label: "Employers/Recipients:",
  priv_s6_li1: "We share only the materials you instruct us to send (email body, resume, cover letter, certificates).",
  priv_s6_li2_label: "Service Providers:",
  priv_s6_li2: "Service providers may include Google/Firebase and Gemini, Vercel, Contabo, DeepSeek, Microsoft, and Tawk.to, depending on the feature you choose.",
  priv_s6_li3_label: "Payment Processors:",
  priv_s6_li3: "We share necessary transaction information with Stripe and Flutterwave to process payments securely. These providers handle payment authorization, settlement, and fraud prevention in accordance with their own privacy and security policies.",
  priv_s6_li4_label: "Legal:",
  priv_s6_li4: "Disclose where required by law or to protect rights, safety, and security.",
  priv_s6_li5: "We do not sell or rent personal data. We avoid unnecessary third‑party sharing.",

  priv_s7_title: "7) Security Measures",
  priv_s7_li1: "We maintain limited operational and security logs for selected actions, including application attempts, usage enforcement, and service errors.",
  priv_s7_li2: "Secrets management and tokenization for OAuth credentials; no storage of Gmail passwords.",
  priv_s7_li3: "We use authentication, Firebase App Check, feature-specific usage limits, and infrastructure-level abuse protections to protect the service.",
  priv_s7_li4: "Access to production systems and provider accounts is restricted to authorized personnel.",

  priv_s8_title: "8) Your Rights",
  priv_s8_li1: "Access, correction, deletion, and portability of your personal data.",
  priv_s8_li2: "Withdraw consent (e.g., revoke Google OAuth) without affecting prior lawful processing.",
  priv_s8_li3: "Object to or restrict processing (subject to applicable law).",
  priv_s8_li4: "Lodge a complaint with a supervisory authority in your jurisdiction.",
  priv_s8_contact: "Contact {email} to request access, correction, deletion, restriction, or a portable copy of your personal data. After verifying your identity, Jobolar will respond within the period required by applicable law and may withhold information where disclosure would affect another person's rights, security, confidentiality, or a legal exemption applies.",

  priv_s9_title: "9) Data Processing Agreements (DPAs) & International Transfers",
  priv_s9_li1: "Where required by applicable law, Jobolar enters into or relies on applicable data-protection terms provided by service providers that process personal data on its behalf.",
  priv_s9_li2: "Providers may process data outside your country. Where required by law, Jobolar relies on applicable contractual or other transfer safeguards made available by those providers.",

  priv_s10_title: "10) Operational & Security Records",
  priv_s10_li1: "We maintain limited records for selected actions, including application attempts, usage enforcement, service errors, and relevant consent or connection events.",
  priv_s10_li2: "These records are used for security, service operation, troubleshooting, and legal compliance—not advertising—and are retained only as reasonably necessary.",

  priv_s11_title: "11) Limitation of Liability",
  priv_s11_li1: "We are not liable for losses due to user negligence (e.g., insecure devices, shared credentials).",
  priv_s11_li2: "No liability for service interruptions or third‑party outages beyond our reasonable control.",
  priv_s11_li3: "No warranty on AI output accuracy, completeness, or suitability; users should review before sending.",
  priv_s11_li4: "To the extent permitted by law, our aggregate liability is limited to fees paid (if any) for the Services.",

  priv_s12_title: "12) Governing Law, Regional Rights & Dispute Resolution",
  priv_s12_intro: "We honor regional privacy rights. Depending on your residence, you may have additional rights under GDPR (EU/UK), CCPA/CPRA (California), LGPD (Brazil), or other local laws.",
  priv_s12_li1_label: "Governing law:",
  priv_s12_li1: "determined by the operating jurisdiction of JOBolar.Ai unless local law requires otherwise.",
  priv_s12_li2_label: "Disputes:",
  priv_s12_li2: "where permissible, resolved via mediation or binding arbitration before court proceedings.",
  priv_s12_li3_label: "Venue & jurisdiction:",
  priv_s12_li3: "exclusive venue consistent with applicable law; users consent to such jurisdiction.",
  priv_s12_contact: "Contact {email} with rights requests or dispute inquiries.",

  priv_s16_title: "13) Contact",
  priv_s16_intro: "Questions about this Privacy Policy or your data? Contact us at:",
  priv_s16_email_label: "Email:",
  priv_s16_address_label: "Physical Address:",

  // ─── REFUND POLICY ───────────────────────────────────
  refund_title: "Refund Policy",
  refund_updated: "Last Updated:",
  refund_date: "August 28, 2026",
  refund_intro: 'This Refund Policy describes when and how subscription fees paid to {company} ("Jobolar", "we", "us") may be refunded. By subscribing to or using our Services, you agree to this policy in addition to our Terms of Service and Privacy Policy.',
  refund_disclaimer: "This document is provided for informational purposes only and does not constitute legal advice. Local laws may grant you additional rights that prevail over any conflicting terms below.",

  refund_s1_title: "1) Scope",
  refund_s1_text: "This policy applies to paid JOBolar.Ai plans and included products, such as job search and matching, eligible Email Auto Apply, Chrome extension features, r\u00e9sum\u00e9 and cover-letter tools, Interview Assistant, and dashboard functionality. It covers initial purchases, renewals, upgrades, downgrades, and cancellations.",

  refund_s2_title: "2) Key Definitions",
  refund_s2_li1_label: "Billing Cycle:",
  refund_s2_li1: "The period covered by your subscription payment (e.g., monthly).",
  refund_s2_li2_label: "Material Use:",
  refund_s2_li2: "Meaningful use of the Service, such as sending multiple job applications via Gmail or generating multiple AI documents.",

  refund_s3_title: "3) Free Trials & Promotions",
  refund_s3_li1: "Trial or promotional periods may be offered from time to time. If a payment method is required, you will be charged when the trial ends unless you cancel beforehand.",
  refund_s3_li2: "Promotional discounts apply only to the specified term. Renewals revert to the then-current standard price unless otherwise stated.",
  refund_s3_li3: "Trial misuse (e.g., repeated sign‑ups) may result in immediate termination without refund.",

  refund_s4_title: "4) Cooling‑Off & Local Rights",
  refund_s4_text: "Customers may have mandatory cancellation, cooling-off, or refund rights under the consumer law that applies to their transaction and jurisdiction. Where those rights apply, JOBolar will honor them in addition to this policy.",

  refund_s5_title: "5) Standard Refund Window (First Purchase)",
  refund_s5_li1: "Within 3 days of your first subscription payment, you may request a refund if you are not satisfied.",
  refund_s5_li2: "Eligibility may be reduced or denied if there has been material use (e.g., multiple email applications sent, substantial AI generation) demonstrating value received.",
  refund_s5_li3: "Refunds for subsequent renewals follow the rules in Section 6.",

  refund_s6_title: "6) Renewals, Upgrades, Downgrades",
  refund_s6_li1_label: "Auto‑Renewal:",
  refund_s6_li1: "Plans renew automatically at the end of each billing cycle unless you cancel before the renewal date.",
  refund_s6_li2_label: "Renewal Refunds:",
  refund_s6_li2: "We generally do not refund renewal charges once the new cycle begins, except where required by law. Contact support promptly for consideration.",
  refund_s6_li3_label: "Upgrades:",
  refund_s6_li3: "Charges are prorated only to apply the additional cost of the higher plan. Refunds are never prorated; once upgraded, fees for the billing cycle are final and non‑refundable.",
  refund_s6_li4_label: "Downgrades:",
  refund_s6_li4: "Take effect at the next renewal date; no prorated or partial refunds are offered for unused time within any billing cycle, except where required by law.",

  refund_s7_title: "7) Non‑Refundable Items",
  refund_s7_li1: "One‑time setup, onboarding, or custom services that have already been delivered.",
  refund_s7_li2: "Third‑party fees, taxes, or currency conversion charges incurred by your bank or payment processor.",
  refund_s7_li3: "Accounts suspended for abuse, policy violations, or fraudulent activity.",
  refund_s7_li4: "Substantial usage during the period (e.g., numerous email applications sent or documents generated), indicating value consumed.",

  refund_s8_title: "8) How to Request a Refund",
  refund_s8_li1: "Contact us at {email} from your registered Gmail address.",
  refund_s8_li2: "Include your full name, account email, order/transaction ID, and the reason for requesting a refund.",
  refund_s8_li3: "We may request additional context (e.g., usage details) to assess eligibility.",
  refund_s8_processing: "Approved refunds are typically processed to the original payment method within 7–10 business days, subject to your bank's timelines.",

  refund_s8a_title: "8A) Payment Provider Processing",
  refund_s8a_intro: "Refunds are issued through the original payment method and processed by our payment partners:",
  refund_s8a_li1_label: "Stripe (card payments):",
  refund_s8a_li1: "typically processed within 5–10 business days depending on your bank.",
  refund_s8a_li2_label: "Flutterwave (bank transfer and mobile money):",
  refund_s8a_li2: "processing times may vary depending on your bank or mobile money provider.",
  refund_s8a_disclaimer: "JOBolar is not responsible for delays caused by external financial institutions or payment providers.",

  refund_s9_title: "9) Cancellations (Stopping Future Charges)",
  refund_s9_li1: "You may cancel at any time from your dashboard. Cancellation prevents future renewals; access continues until the current billing period ends.",
  refund_s9_li2: "Deleting your account or revoking OAuth does not automatically cancel billing—please cancel your plan in the dashboard to stop future charges.",
  refund_s9_li3: "No proration: Cancelling in the middle of a billing period does not entitle you to a credit or refund for unused time, regardless of cycle length.",

  refund_s10_title: "10) Disputed Charges & Chargebacks",
  refund_s10_li1: "If you believe a charge is incorrect, contact {email} before filing a chargeback. We will investigate promptly.",
  refund_s10_li2: "Unauthorized or fraudulent transactions will be refunded when verified. Repeated or abusive chargebacks may lead to account suspension.",

  refund_s11_title: "11) Compliance & Exceptions",
  refund_s11_li1: "Where applicable consumer law provides non-waivable protections, those protections apply regardless of this policy.",
  refund_s11_li2: "We reserve the right to make case‑by‑case exceptions in good faith (for example, documented system outages or duplicate billing).",

  refund_s12_title: "12) Changes to this Policy & Contact",
  refund_s12_text: "We may update this Refund Policy to reflect changes in our Services, pricing, or applicable law. Material changes will be notified via the dashboard or email. The Effective Date above indicates the latest version.",
  refund_s12_contact: "Questions? Contact {billing_email} or {support_email}.",
},

// ─── FAQ ───────────────────────────────────────────────
faq: {
  title: "FAQ",
  faq_label: "JOBolar FAQ",
  search_placeholder: "Search FAQs...",
  clear: "Clear",
  no_results: "No results found for",
  legal_faq: "Legal FAQ",
  // AI FAQ Buddy
  ai_faq_buddy: "AI FAQ Buddy",
  ai_faq_desc: "Your FAQ search assistant",
  live: "Live",
  ask_placeholder: "Ask about the FAQ\u2026",
  searching: "Searching FAQ\u2026",
  not_found: "I couldn\u2019t find that in the FAQ.",
  not_found_detail: "I couldn\u2019t find that in the FAQ. Try rephrasing your question or ask about a different topic.",
  initial_msg: "Hi there! I\u2019m your JOBolar FAQ buddy. Ask anything from our FAQ and I\u2019ll pull the relevant entry.",
  // Sample questions
  sample_q1: "How do I sign up?",
  sample_q2: "What permissions are required?",
  sample_q3: "Can I pause applications?",
  // FAQ Q&A (27 pairs)
  q1: "How do I sign up for JOBolar.Ai?",
  a1: "Sign up with your Google Account, then choose the Jobolar products you want to use. Connecting Gmail sending access is optional and is required only if you enable Email Auto Apply.",
  q2: "When does JOBolar.Ai require Gmail access?",
  a2: "Only Email Auto Apply requires a connected Gmail account and the Gmail send permission. Job search and matching, the Chrome extension, r\u00e9sum\u00e9 tools, and Interview Assistant do not require Gmail sending access.",
  q3: "How does JOBolar.Ai handle browser applications?",
  a3: "Email Auto Apply does not log in to job portals and runs only for eligible postings with a valid public employer or recruiter email. Separately, the Jobolar Chrome extension can fill and, where supported, submit browser application forms inside your active browser session. You can review the application and use the available pause, resume, and cancel controls.",
  q4: "What permissions does JOBolar.AI request from Google?",
  a4: "JOBolar.AI requests the following permissions:\n1. Send emails on your behalf (gmail.send).\n2. See your primary Gmail address.\n3. Access your basic Google profile (name, profile picture, and language).\n\nThese permissions come from standard Google Sign-In and are used only for authentication and personalizing your experience. We do not access your Gmail inbox or any other message data.",
  q5: "Do you store my Gmail password or read my private emails?",
  a5: "Never. JOBolar.Ai uses secure OAuth authentication, which means we never see or store your Gmail password.\nWe also do not read, track, or analyze your private emails.\nAccess is limited to sending job applications with your explicit consent.",
  q6: "What does \u2018Token stored (server)\u2019 mean?",
  a6: "This means we store an encrypted Gmail refresh token on our server. The token is used only to send user-triggered job-application emails on your behalf.\nWe do not store your Gmail password at any time.\nThe token is encrypted at rest for security and is deleted immediately when you disconnect Gmail or revoke access.",
  q7: "What happens when I disconnect Gmail?",
  a7: "When you disconnect Gmail:\n\u2022 Your encrypted refresh token is deleted from our servers instantly and permanently.\n\u2022 JOBolar immediately stops sending emails on your behalf.\n\u2022 You can reconnect Gmail at any time to resume using the email-sending features.\n\u2022 You can also revoke access via your Google Account settings at https://myaccount.google.com/permissions.",
  q8: "What happens after revoking access?",
  a8: "After revoking access:\n\u2022 The encrypted token is deleted permanently from our servers.\n\u2022 JOBolar cannot send emails on your behalf until you reconnect Gmail.\n\u2022 All your other data (profile, resumes, cover letters) remains intact unless you delete them separately.",
  q9: "How do you get my email address and what do you use it for?",
  a9: "When you sign in with Google, your Gmail address is securely shared with JOBolar.Ai.\nWe only use this email to:\n\u2022 Send job applications.\n\u2022 Provide updates.\n\u2022 Deliver daily summaries.\nWe never sell or share your email for advertising or marketing.",
  q10: "What kind of data does JOBolar.Ai store about me?",
  a10: "We store your user profile, uploaded resumes, generated cover letters, and basic metadata such as job titles, dates, and platforms where you\u2019ve applied.\nThis helps us track your progress and display your application history within the dashboard.\nWe do not use Gmail data to train models or serve ads. Gmail data is used solely for sending job applications you initiate.",
  q11: "Where is my data stored and how do you protect it?",
  a11: "All user data is securely stored in Firebase (Google Cloud).\nData is encrypted both in transit and at rest.\nOnly you can access your files and application logs.\nOur team cannot access your personal files without your authorization.",
  q12: "Can I delete my data or close my JOBolar.Ai account?",
  a12: "Yes. You can permanently delete your account, revoke Google access, and remove your data through your dashboard or directly via your Google Account\u2019s permissions page.\n\nWhen you delete your JOBolar account, we permanently delete your profile, resumes, cover letters, job application history, and associated files from our Firebase Storage and databases. They are removed from our systems and are no longer accessible through JOBolar.\n\nOur cloud provider may retain short-lived, encrypted backups for disaster recovery, which are time-limited and not accessible or used by JOBolar.",
  q13: "Can I apply to jobs locally and abroad?",
  a13: "Yes! JOBolar.Ai lets you apply to roles in your home country or overseas.\nYou can filter jobs by region and apply globally from the comfort of your home using the same Gmail account.",
  q14: "How does JOBolar.Ai help me apply faster?",
  a14: "Our system sends out your AI-enhanced resume, cover letter, and certificates automatically through Gmail.\nYou can apply to dozens of jobs in minutes\u2014significantly faster than manual submission\u2014and be among the first to reach employers.",
  q15: "What is AI Chatty Buddy (JOBolar AI)?",
  a15: "AI Chatty Buddy is your built-in AI assistant that helps you create or improve your resume, generate cover letters, and tailor each application automatically.\nIt ensures every job submission feels personal and professional.",
  q16: "How fast can JOBolar.Ai send applications?",
  a16: "JOBolar.Ai can send multiple applications within seconds, depending on your daily limits.\nSpeed and volume can be adjusted from your dashboard to avoid exceeding Gmail\u2019s safe sending thresholds.",
  q17: "Can I pause or limit my applications?",
  a17: "Absolutely. You can:\n\u2022 Set a daily cap for how many applications to send.\n\u2022 Pause the automation.\n\u2022 Resume it anytime via your JOBolar dashboard.",
  q18: "What are daily email summaries?",
  a18: "JOBolar.Ai sends a daily summary to your Gmail showing:\n\u2022 How many jobs were applied to.\n\u2022 Links to employers.\n\u2022 Any replies received.\nThis helps you stay organized without logging into multiple sites.",
  q19: "How can I unsubscribe from JOBolar emails?",
  a19: "Every JOBolar notification email includes an unsubscribe link.\nYou can also manage communication settings in your dashboard to opt out of specific types of emails.",
  q20: "Do you share my personal information with employers?",
  a20: "We only share what\u2019s necessary for the job application:\n\u2022 Your resume.\n\u2022 Cover letter.\n\u2022 Email address.\nWe do not sell or share your data with any third parties for marketing or advertising.",
  q21: "Is JOBolar.Ai available on mobile?",
  a21: "Yes. You can use JOBolar.Ai on any mobile browser to manage your dashboard and track applications.\nA dedicated mobile app is under development for future release.",
  q22: "Can I get a refund or cancel my plan?",
  a22: "Yes, we offer a flexible refund policy.\nYou can cancel your subscription anytime, and refunds are processed as outlined in our Refund Policy section.",
  q23: "What happens to my billing and payment records if I delete my account?",
  a23: "If you purchase a paid plan, our payment processors (Stripe for card payments and Flutterwave for bank transfer and mobile money) collect and store your billing information (such as your name, billing email, and payment method details) to process payments, prevent fraud, and comply with financial and tax regulations.\n\nWhen you delete your JOBolar account, we delete or de-identify your account data in our systems. However, we may retain limited transaction and billing records as required by law (for example, for tax, accounting, or audit purposes). These records are not used for marketing and are stored securely by our payment providers.",
  q24: "How can I contact support, billing, or legal?",
  a24: "You can reach us anytime at:\n\u2022 Support: support@JOBolar.Ai\n\u2022 Billing: billing@JOBolar.Ai\n\u2022 Legal: legal@JOBolar.Ai",
  q25: "Where is JOBolar.Ai located?",
  a25: "Our office is located at:\n330 S Second Ave, Suite 200 #1319\nMinneapolis, MN 55401\nUnited States\n\nYou can reach us via email at support@JOBolar.Ai or through live chat on our website.",
  q26: "Does JOBolar follow Gmail\u2019s daily sending limits?",
  a26: "Yes. Email Auto Apply is paced and capped so it does not intentionally exceed the sending limits applied to the connected Gmail account. Google may change or enforce its own limits, and Jobolar cannot guarantee delivery.",
  q27: "What is the number of prompts I get to use per day on AI Chatty Buddy?",
  a27: "You can use up to {maxAi} prompts per day. After that, you\u2019ll need to wait until the next 24-hour period.",
},

// ─── Profile Menu / Dashboard ──────────────────────────
profile: {
  account_settings: "Account Settings",
  account_menu: "Account menu",
  profile: "Profile",
  dashboard: "Dashboard",
  billing: "Billing & Subscription",
  automation: "Automation Settings",
  privacy_security: "Privacy & Security",
  interview_copilot: "Interview Assistant",
},

// ─── Billing Tab ───────────────────────────────────────
billing: {
  title: "Billing & Subscription",
  subtitle: "Manage your subscription plan and billing information",
  current_plan: "Current Plan",
  free: "Free",
  active: "Active",
  canceled: "Canceled",
  expired: "Expired",
  status_label: "Status",
  via_provider: "via",
  next_billing: "Next Billing Date",
  update_payment: "Update payment method",
  update_payment_desc: "Change your card or billing details via Stripe",
  change_payment: "Change payment method",
  resubscribe_fwave: "Re-subscribe with a different Flutterwave method (bank transfer, mobile money, etc.)",
  cancel_subscription: "Cancel Subscription",
  stop_renewal: "Stop Renewal",
  stop_renewal_question: "Stop Renewal?",
  cancel_sub_question: "Cancel Subscription?",
  stop_renewal_confirm: "Are you sure you want to stop your subscription from renewing? You\u2019ll keep access until the end of your current billing period. Flutterwave does not auto-charge, so no further payments will be taken.",
  cancel_confirm: "Are you sure you want to cancel your subscription? You\u2019ll continue to have access until the end of your current billing period.",
  cancel_keep: "Keep Subscription",
  canceling: "Canceling...",
  yes_stop_renewal: "Yes, Stop Renewal",
  yes_cancel: "Yes, Cancel",
  cancel_success: "Subscription canceled successfully.",
  sub_canceled: "Subscription canceled",
  sub_canceled_desc: "Your subscription is canceled and will not renew. You will continue to have access until {date}.",
  access_until: "Access continues until:",
  stop_renewal_desc: "Stop your Flutterwave subscription from renewing. You\u2019ll keep access until the end of your current billing period.",
  cancel_sub_desc: "You can cancel your subscription at any time. You\u2019ll continue to have access until the end of your billing period.",
  no_subscription: "No active subscription",
  no_subscription_desc: "You don't have an active plan. Choose a plan to get started.",
  choose_plan: "Choose a plan",
  upgrade_plan: "Upgrade Your Plan",
  upgrade_desc: "Get access to premium features and unlimited job applications.",
  view_pricing: "View Pricing",
  view_all_plans: "View All Plans \u2192",
  change_plan_question: "Need to change your plan?",
  change_plan_desc: "Visit our pricing page to upgrade or downgrade your subscription.",
  manual_renewal: "Manual renewal (Flutterwave)",
  manual_renewal_desc: "This payment method does not auto-renew. We\u2019ll remind you before your plan ends.",
  plan_ending_soon: "Plan ending soon \u2014 your access ends on {date}.",
  expired_on: "Expired on",
  access_ends: "Access ends (Manual renewal)",
  billed: "Billed",
  renew_now: "Renew now",
  change_plan: "Change plan",
  renew_early_desc: "Renewing early extends your access from your current end date \u2014 no time lost.",
  plan_expired: "Your plan has expired",
  reactivate_desc: "Reactivate to regain access to premium features.",
  reactivate: "Reactivate",
  switch_stripe: "Switch to Stripe (Auto-renew)",
  subscribe_stripe: "Subscribe with Stripe to manage your payment method.",
  portal_loading: "Opening\u2026",
  loading: "Loading\u2026",
  error_fetch: "Failed to fetch subscription status",
  error_portal: "Failed to open billing portal",
  error_consent: "Unable to load Gmail consent settings.",
  error_sign_in: "Please sign in first.",
  error_save_consent: "Could not save consent.",
  error_cancel: "Failed to cancel subscription.",
  error_no_sub: "No active subscription to cancel",
  error_disable_gmail: "Failed to disable Gmail sending.",
  gmail_disabled_success: "Gmail sending disabled successfully.",
},

// ─── Automation Tab ────────────────────────────────────
automation: {
  title: "Automation Settings",
  subtitle: "Configure how JOBolar automatically applies to jobs on your behalf",
  settings_saved: "Settings saved successfully",
  enable_title: "Enable Automation",
  enable_desc: "Allow JOBolar to automatically apply to jobs based on your preferences and resume",
  automation_active: "Automation is active. JOBolar will apply to jobs matching your preferences.",
  automation_off: "Automation is currently off. Turn it on to let JOBolar work for you.",
  paused_for: "Paused for",
  resumes_in: "Resumes in",
  turn_back_on: "Turn automation back on at any time.",
  power_on: "Power On",
  power_off: "Power Off",
  automation_live: "Automation is live",
  tap_activate: "Tap to activate automation",
  on: "On",
  off: "Off",
  quick_pause: "Quick pause:",
  pause_day: "{count} day",
  pause_days: "{count} days",
  duration_day_short: "d",
  duration_hour_short: "h",
  duration_minute_short: "m",
  max_jobs_title: "Maximum Jobs Per Day",
  max_jobs_desc: "Set the maximum number of jobs JOBolar can apply to on your behalf each day",
  custom_max: "Custom:",
  max_jobs_note: "Recommended: 5\u201320 jobs per day. Gmail limits standard accounts to 500 emails per day, so JOBolar will always cap your applications at 500 per day.",
  daily_email_title: "Daily Email Report",
  daily_email_desc: "Receive a daily email report of all job applications submitted on your behalf",
  daily_report_notice: "You\u2019ll receive a daily report email at {email}",
  pause_expired: "Pause expired",
  load_error: "Unable to load automation settings. Please try again.",
  save_error: "Unable to save automation settings. Please try again.",
  decrease_max_jobs: "Decrease maximum jobs per day",
  increase_max_jobs: "Increase maximum jobs per day",
  max_jobs_input: "Maximum jobs per day",
  how_it_works_title: "How automation works",
  how_it_works_1: "JOBolar scans job sites daily for positions matching your preferences",
  how_it_works_2: "Your resume and profile are automatically submitted to qualifying jobs",
  how_it_works_3: "Applications are tracked in your dashboard for easy follow-up",
  how_it_works_4: "You can pause or adjust automation settings at any time",
  saving: "Saving...",
  page_title: "Automation",
  page_subtitle: "Let JOBolar automatically apply to high-match roles while you focus on interviews.",
},

// ─── Analytics Tab ─────────────────────────────────────
analytics: {
  loading: "Loading analytics\u2026",
  error_title: "Unable to load job analytics.",
  load_error: "Please try again. If the problem continues, contact Jobolar support.",
  report_processing: "Your report is currently being processed and will be ready within 3 days after submission and payment.",
  badge: "Application analytics",
  badge_analytics: "Analytics",
  hero_title: "Track progress at a glance",
  hero_desc: "Monitor your application activity over time \u2014 from Today to the Past 6 Months.",
  today: "TODAY",
  past_7: "PAST 7 DAYS",
  past_30: "PAST 30 DAYS",
  past_6m: "PAST 6 MONTHS",
  daily_email_label: "Subscribe to daily email report",
  date_period: "Date Period",
  quick_7_days: "7d",
  quick_30_days: "30d",
  all_periods: "All",
  custom_range: "Custom Date Range",
  use_custom_range: "Use custom date range",
  from: "From:",
  to: "To:",
  from_date: "From date",
  to_date: "To date",
  clear: "Clear",
  job_title: "Job Title",
  company: "Company",
  source: "Source",
  filter_job_title: "Filter by job title",
  filter_company: "Filter by company",
  filter_source: "Filter by source",
  filter_method: "Filter by application method",
  method: "Method",
  all_methods: "All methods",
  email_auto_apply: "Email Auto Apply",
  auto_apply: "Auto Apply",
  copilot_assist: "Copilot Assist",
  email_sent: "Email Sent",
  job_link: "Job Link",
  date: "Date",
  clear_filters: "Clear Filters",
  clear_all_filters: "Clear all filters",
  view_email: "View email",
  view_posting: "View posting",
  view_email_aria: "View email sent for {job} at {company}",
  view_posting_aria: "View posting for {job} at {company}",
  unknown_job: "this job",
  unknown_company: "company",
  empty_title: "No matching applications yet",
  empty_desc: "Applications will appear here as they are recorded. Adjust your filters if you expected to see earlier activity.",
},

// ─── Privacy Tab ───────────────────────────────────────
privacy_tab: {
  title: "Privacy & Security",
  subtitle: "We take your privacy seriously. This page explains what we collect, how we use it, and how you can control access.",
  what_collect: "What we collect",
  collect_1: "Profile information you provide (name, email, location, resume data).",
  collect_2: "Usage data to improve the product and provide analytics.",
  collect_3: "We collect your Gmail email address only after you explicitly connect Gmail. We never store your Gmail password.",
  how_use: "How we use your data",
  use_1: "To provide and improve JOBolar features (resume uploads, job recommendations, automation).",
  use_2: "To process payments and maintain billing records with our payment processors (Stripe for card payments and Flutterwave for bank transfer and mobile money in supported regions).",
  use_3: "If you connect Gmail, we use the gmail.send scope only to send job application emails that you trigger. We do not read, access, or store your inbox or any Gmail content.",
  data_retention: "Data Retention",
  retention_1: "If you delete your JOBolar account, active profile data, resumes, preferences, application history, and connected-service tokens are removed from Jobolar systems. Limited encrypted disaster-recovery backups, security logs, and records required by law may remain for restricted retention periods.",
  retention_2: "For legal and accounting reasons, our payment processors (Stripe and Flutterwave) may retain limited billing and transaction records, but these are not used for marketing.",
  retention_3: "Payment data is handled securely by Stripe and Flutterwave. JOBolar does not store sensitive financial details such as card numbers or bank account credentials. Both Stripe and Flutterwave are industry-recognized payment providers operating under strict security, encryption, and regulatory standards.",
  gmail_access: "Gmail Access & Revocation",
  gmail_1: "You can disable Gmail sending at any time inside JOBolar.",
  gmail_2: "You can also revoke JOBolar\u2019s access directly from your Google Account:",
  google_permissions: "Google permissions",
  gmail_3: "When access is revoked, all Gmail tokens are deleted immediately and JOBolar cannot send any emails on your behalf.",
  account_deletion: "Account deletion (danger zone)",
  deletion_desc: "Deleting your account removes access and starts permanent deletion of active account data, subject to the limited backup, security, transaction, and legal-retention exceptions described above.",
  privacy_resources: "Privacy Resources",
  privacy_policy_link: "Privacy Policy \u2192",
  terms_link: "Terms of Service \u2192",
  footer_text: "If you\u2019d like to share feedback privately, you may reply to any JOBolar email or contact",
},

// ─── Delete Account ────────────────────────────────────
delete_account: {
  button: "Delete account",
  title: "Delete account (danger zone)",
  desc: "Deleting your account will permanently and immediately delete all personal data, including resumes, job preferences, application history, and Gmail tokens.",
  warning_title: "Warning: This action is permanent",
  warning_desc: "Deleting your account will:",
  warn_1: "Cancel any active subscriptions linked to this account",
  warn_2: "Permanently delete your resumes, profiles, and job preferences",
  warn_3: "Remove all job applications and tracking data from your dashboard",
  warn_4: "Delete all certifications and saved links",
  warn_5: "Revoke your Gmail connection and delete all stored Gmail tokens",
  warn_6: "Permanently delete your JOBolar account and sign you out",
  confirm_prompt: "To confirm deletion, please type {keyword} below. This action is permanent and cannot be undone.",
  confirm_placeholder: "Type \"{keyword}\" to confirm",
  confirm_error: "Please type \"{keyword}\" to confirm.",
  success: "Your account has been deleted.",
  error_default: "Failed to delete account. Please try again.",
  deleting: "Deleting\u2026",
},

// ─── Payment ───────────────────────────────────────────
payment: {
  // Shared
  cancel: "Cancel",
  close: "Close",
  continue_btn: "Continue",
  pay_with_card: "💳 Pay with Card (Stripe)",
  card_any_country: "Use debit/credit card from any country",
  secure_flutterwave: "All payment options are secure and verified by Flutterwave",
  secure_flutterwave_short: "Secure payment via Flutterwave",
  checking_payment: "Checking payment…",
  auto_checking: "Automatically checking for payment confirmation…",
  timeout_message: "Still confirming. Please wait a few minutes and try again, or contact support.",
  i_have_paid: "I have paid",
  amount_to_pay: "Amount to Pay",
  payment_failed: "Payment Failed",
  retry_payment: "Retry Payment",
  contact_support: "Contact Support",

  // PaymentMethodModal
  choose_method: "Choose Payment Method",
  flutterwave_tab: "Flutterwave (Bank Transfer / Mobile Money)",
  recommended_region: "Recommended for your region",
  stripe_tab: "Stripe (Card)",
  recommended_cheapest: "Recommended & Cheapest",
  africa_friendly: "Africa-Friendly Payment Options",
  africa_friendly_desc: "Pay using Africa-friendly options like bank transfer or mobile money (varies by country).",
  processing_fee: "May include a small processing fee depending on your bank/provider.",
  country_not_supported_1: "If your country is not listed, it is not currently supported by Flutterwave. Please use",
  country_not_supported_2: "instead.",
  select_country: "Select Your Country *",
  search_country: "Search or select a country…",
  select_country_desc: "Select the country where you'll be making the payment from.",
  select_mobile_provider: "Select your mobile money provider to continue",
  providers_unavailable: "Mobile Money providers could not be loaded. Use Card (Stripe) or try again later.",
  select_provider_title: "Select Mobile Money Provider",
  provider_not_found: "Can’t find your provider?",
  country_not_listed: "Country not listed?",
  francophone_west_africa: "Francophone West Africa",
  francophone_central_africa: "Francophone Central Africa",
  payment_option: "payment option",
  choose_how_to_pay: "Choose how you'd like to pay, then click Continue.",
  global_support: "Global Payment Support",
  global_support_desc: "Pay securely with your debit or credit card from any country worldwide.",
  instant_activation: "Fast & Instant Activation",
  instant_activation_desc: "No bank transfer delays. Your subscription is activated immediately after payment.",
  bank_security: "Bank-Level Security",
  bank_security_desc: "Your card details are never stored on Jobolar servers. They are encrypted and securely processed by Stripe, a global payment leader with industry-standard security and 3D Secure authentication.",
  learn_more: "Learn more:",
  stripe_security_doc: "Stripe Security Documentation",
  trusted_worldwide: "Trusted by millions of businesses worldwide.",

  // BankTransferDetails
  complete_payment: "Complete Your Payment",
  flw_bank_unavailable: "Flutterwave Bank Transfer Unavailable",
  transfer_exact_desc: "Transfer the exact amount to the account details provided",
  bank_name: "Bank Name",
  account_number: "Account Number",
  amount_to_transfer: "Amount to Transfer",
  secure_payment_note: "Secure Payment:",
  secure_payment_note_desc: "This is a secure temporary account provided by Flutterwave. The bank name may be unfamiliar, but transfers are safe and verified.",
  copied_account: "Copied account number successfully!",
  copied_amount: "Copied amount successfully!",
  note_label: "Note:",
  account_expires: "This account expires on {date}",
  important_instructions: "Important Instructions:",
  transfer_exact_amount: "Transfer the exact amount shown above",
  confirm_5_10_min: "Payment is usually confirmed within 5-10 minutes",
  click_i_have_paid: "Click \"I have paid\" after making the transfer",
  keep_page_open: "Keep this page open until payment is confirmed",
  email_support: "📧 Email Support ({email})",
  flw_issue_subject: "Flutterwave Payment Issue",
  flw_issue_body: "I need help with a Flutterwave payment issue.",

  // MobileWalletDetails
  verifying_payment: "Verifying Payment",
  authorize_payment: "Authorize Payment",
  waiting_approval: "Waiting for Approval",
  pay_with_wallet: "Pay with {wallet}",
  pending_checking: "We found a pending payment — checking for confirmation.",
  complete_auth_phone: "Complete the authorization on your phone to activate your subscription",
  click_authorize_desc: "Click the button below to authorize this payment",
  approve_phone: "Approve the payment on your phone",
  complete_in_app: "Complete payment in your {wallet} app",
  payment_instructions: "Payment Instructions",
  how_to_pay: "How to Pay",
  status_label: "Status",
  click_authorize_below: "Click \"Authorize Payment\" below",
  approve_momo_prompt: "Approve the Mobile Money prompt on your phone",
  keep_open_60s: "Keep this page open — we confirm automatically (up to 60 s)",
  no_prompt_retry: "If no prompt within 60 s, retry or change your network/number",
  open_wallet_below: "Click \"Open {wallet}\" below",
  complete_in_app_step: "Complete payment in the app",
  return_click_paid: "Return here and click \"I have paid\"",
  scan_qr_pay: "Scan QR to Pay",
  scan_authorize: "Scan to Authorize",
  copied: "Copied!",
  copy_link: "Copy link",
  check_phone_momo: "Check your phone for a Mobile Money prompt.",
  re_open_auth: "Re-open Authorization",
  waiting_auth: "Waiting for authorization…",
  open_link_again: "Open link again",
  checking_confirmation: "Checking for confirmation…",
  confirmation_pending: "Confirmation pending",
  auth_not_completed: "Authorization was not completed. Please try again.",
  timeout_short: "Still confirming. Wait a few minutes and try again, or contact support.",
  waiting_to_authorize: "Waiting for you to authorize the payment",
  open_wallet: "Open {wallet}",
  resuming: "Resuming",

  // FlutterwaveLoadingOverlay
  payment_temp_unavailable: "Payment Temporarily Unavailable",
  payment_unavailable: "Payment Unavailable",
  couldnt_process: "We couldn't process your payment. Please try again.",
  try_again: "Try Again",
  generating_account: "Generating payment account",
  take_up_to: "This can take up to {seconds} seconds. Please don't close this window.",
  pct_complete: "{pct}% complete",
  still_working: "Still working… please keep this window open.",
  elapsed: "Elapsed: {s}s",

  // UssdDetails
  pay_with_ussd: "Pay with USSD",
  dial_ussd_desc: "Dial the USSD code below on your phone to complete payment",
  dial_this_code: "Dial This Code",
  ussd_copied: "USSD code copied successfully!",
  bank_label: "Bank",
  how_to_pay_ussd: "How to Pay with USSD:",
  open_dialer: "Open your phone's dialer",
  dial_code_above: "Dial the USSD code shown above",
  follow_prompts: "Follow the prompts on your phone",
  click_i_dialed: "Click \"I have dialed\" after initiating payment",
  confirm_2_5_min: "Payment confirmation usually takes 2-5 minutes",
  no_internet_needed: "✓ No internet needed:",
  no_internet_desc: "USSD works on any phone, even without data or WiFi.",
  i_have_dialed: "I have dialed",

  // CompleteProfilePrompt
  complete_profile: "Complete Your Profile",
  complete_profile_desc: "Complete your profile to use Flutterwave payments.",
  add_following: "Please add the following to continue:",
  email_address: "Email address",
  phone_number: "Phone number",
  first_name: "First name",
  last_name: "Last name",
  go_to_profile: "Go to Profile",
  or_card_payment: "Or use card payment",
  card_no_phone: "Card payments don't require phone number",

  // AlternatePaymentOptions
  having_trouble: "Having trouble? Try another option:",
  transfer_to_bank: "Transfer to a bank account",
  pay_momo_wallet: "Pay with your mobile money wallet",
  pay_opay_wallet: "Pay from your OPay mobile wallet",

  // CountryPhonePrompt
  phone_mismatch: "Phone / country mismatch",
  flw_requires: "Flutterwave {country} options require:",
  phone_number_label: "Phone number:",
  profile_country_label: "Profile country:",
  your_details: "Your details:",
  phone_label: "Phone:",
  selected_payment_country: "Selected payment country:",
  phone_calling_code: "Phone calling code:",
  not_set: "not set",
  update_phone_desc: "Update your phone number or profile country, or choose another payment method.",
  update_phone_btn: "Update phone number or country",
  or_alt_payment: "Or use alternative payment",
  works_any_phone: "Works with any phone number",
  use_bank_transfer: "🏦 Use Bank Transfer instead",

  // Country phone prompt titles
  nigeria_phone_title: "Nigeria payments require matching phone + profile country",
  ghana_phone_title: "Ghana payments require matching phone + profile country",
  kenya_phone_title: "Kenya payments require matching phone + profile country",
  south_africa_phone_title: "South Africa payments require matching phone + profile country",
  egypt_phone_title: "Egypt payments require matching phone + profile country",
  uganda_phone_title: "Uganda payments require matching phone + profile country",
  tanzania_phone_title: "Tanzania payments require matching phone + profile country",
  rwanda_phone_title: "Rwanda payments require matching phone + profile country",
  zambia_phone_title: "Zambia payments require matching phone + profile country",
  francophone_phone_title: "{country} payments require matching phone + profile country",
},

// ─── Pricing ────────────────────────────────────────────
pricing: {
  heading: "Simple pricing, smarter outreach",
  season_promo_1: "Season Promo:",
  off: "OFF",
  season_promo_2: "— your discounted price is already applied below.",
  use_code: "Use code:",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Semiannual",
  short_mo: "mo",
  short_qr: "qr",
  short_sa: "sa",
  choose_plan: "Choose Plan",
  loading_text: "Loading…",
  cancel_note: "Cancel anytime • No hidden fees",
  save_pct: "SAVE {pct}%",
  currency_note: "All currency values are in USD. Your discounted price is already applied. The crossed-out price reflects the pre-discount anchor.",
  plan_name_basic: "Basic",
  plan_name_standard: "Standard",
  plan_name_pro: "Pro",
  plan_tag_basic: "Starter Plan",
  plan_tag_standard: "Best Value",
  plan_tag_pro: "Power Users",
  plan_mode_basic: "JOBolar emails job links to you based on your preferences.",
  plan_mode_standard: "JOBolar sends your applications to employers using your static resume with an AI-generated cover letter. You'll also receive alerts for jobs that require manual application.",
  plan_mode_pro: "JOBolar sends your applications to employers dynamically using AI-tailored resumes and AI cover letters with each job. You'll also receive alerts for jobs that require manual application.",
  feat_daily_ai: "Daily AI job search & matching",
  feat_curated: "Curated job links",
  feat_analytics: "Application analytics dashboard",
  feat_unlimited: "Unlimited job alerts",
  feat_direct_links: "Direct job links emailed to you",
  feat_priority: "Priority queue",
  feat_cover_letter: "AI cover letter",
  feat_auto_send: "Automatic application sending via email",
  feat_cover_included: "AI cover letter included",
  feat_resume_cover: "Emails include your uploaded static resume and AI-generated cover letter",
  feat_manual_alerts: "Get alerts for manual application opportunities with AI assistance",
  feat_dynamic_resume: "AI dynamically updates your resume and cover letter per job",
  alert_error: "An error occurred. Please try again.",
  // Confirmation modal
  confirm_title: "Quick Confirmation Before You Continue",
  confirm_subtitle: "This takes 5 seconds — just to make sure Jobolar fits your needs.",
  confirm_c1_before: "I understand that Jobolar is an automation tool that helps me apply for jobs and ",
  confirm_c1_bold: "does NOT guarantee job offers or employment",
  confirm_c2_before: "I understand that Jobolar can only apply to jobs that are ",
  confirm_c2_bold1: "publicly available online",
  confirm_c2_mid: " and include a ",
  confirm_c2_bold2: "valid email for application submission",
  confirm_c3_before: "I understand that some jobs may not be automatically applied to if they require ",
  confirm_c3_bold: "external application portals",
  confirm_c3_after: " (e.g., company websites without email submission).",
  confirm_c4_before: "I understand that in such cases, Jobolar may instead ",
  confirm_c4_bold: "send me job recommendations",
  confirm_c4_after: " so I can apply manually.",
  confirm_c5_before: "I understand that results may vary depending on ",
  confirm_c5_bold: "job availability, my profile, and market conditions",
  confirm_btn_accept_all: "Accept All & Continue",
  confirm_btn_continue: "Continue to Payment",
  confirm_footer: "If you are unsure whether Jobolar fits your needs, we recommend starting with the free trial.",
  confirm_purchase_title_named: "{name}, confirm your {plan} purchase",
  confirm_purchase_title: "Confirm your {plan} purchase",
  confirm_purchase_summary: "{plan} · {duration} · {price} · {provider}",
  confirm_subtitle_compact: "Please review these five points before payment.",
  confirm_provider_stripe: "Stripe auto-renewal",
  confirm_provider_flutterwave: "Flutterwave prepaid",
  confirm_term_monthly: "1 month",
  confirm_term_quarterly: "3 months",
  confirm_term_semiannual: "6 months",
  confirm_shared_outcome: "Jobolar assists with job searching, applications and interviews but does not guarantee interviews, job offers or employment.",
  confirm_shared_ai_rules: "AI suggestions may contain mistakes. I am responsible for reviewing them and following employer, interviewer and platform rules.",
  confirm_basic_extension: "Basic includes the Chrome extension with unlimited deterministic Copilot Fill, but not Smart Apply, Email Auto Apply, AI application drafts, résumé tailoring, scheduled applications or automatic submissions.",
  confirm_basic_benefits: "Basic includes daily job matching, alerts, emailed job links and analytics, plus {liveMinutes} Live and {mockMinutes} Mock Interview Assistant minutes per billing cycle.",
  confirm_standard_extension: "Standard includes Chrome Smart Apply, scheduled Auto Apply and unlimited Copilot Fill on supported job sites, including Indeed and LinkedIn; other available sites vary by country and posting. Limits are 200 confirmed submissions per billing cycle and 20 per day.",
  confirm_standard_email: "Email Auto Apply works for eligible postings with a valid public employer or recruiter email in {countryCount} supported countries, using my unchanged Base Resume. Elsewhere I can use Chrome Smart Apply on supported sites; Smart Tailor and Deep Tailor are not included.",
  confirm_standard_allowances: "Standard includes {liveMinutes} Live and {mockMinutes} Mock Interview minutes and up to 200 AI application drafts per billing cycle. AI content needs my review and must follow employer, interviewer and platform rules.",
  confirm_pro_extension: "Pro includes complete Chrome Smart Apply, scheduled Auto Apply and unlimited Copilot Fill on supported job sites, including Indeed and LinkedIn; other available sites vary by country and posting. Limits are 300 confirmed submissions per billing cycle and 20 per day.",
  confirm_pro_email: "Email Auto Apply works for eligible postings with a valid public employer or recruiter email in {countryCount} supported countries, using job-specific AI-tailored résumés and cover letters. Elsewhere I can use Chrome Smart Apply on supported sites.",
  confirm_pro_allowances: "Pro includes Base Resume, Smart Tailor and Deep Tailor (verified Career Profile required), {liveMinutes} Live and {mockMinutes} Mock Interview minutes, and up to 300 AI drafts per billing cycle. AI content needs my review and must follow platform rules.",
  confirm_copilot_outcome: "Interview Assistant provides AI interview assistance but does not guarantee interview success, job offers or employment.",
  confirm_copilot_features: "It includes Manual and Smart Listen, live transcription, talking points and suggested answers on Jobolar Web, Chrome’s visible Translucent Focus Overlay, and Desktop, including the screen-share-invisible Desktop experience.",
  confirm_copilot_exclusions: "This plan does not include job searching, Email Auto Apply, Chrome Smart Apply, résumé automation or job-application submissions.",
  confirm_copilot_allowance: "I am purchasing {hours} hour(s) per month. Minutes reset monthly without rollover; I must adapt AI suggestions and follow interviewer, employer and platform rules.",
  confirm_billing_stripe: "I authorize Jobolar through Stripe to charge {price}, plus applicable tax, every {term} until I cancel. Cancellation stops future renewals.",
  confirm_billing_flutterwave: "This is a one-time prepaid Flutterwave payment equivalent to {price} for {term}. Local currency conversion may apply, and it will not renew automatically.",
  confirm_footer_compact: "Use Accept All & Continue to select all five confirmations.",
},

// ─── Common / Shared ───────────────────────────────────
common: {
  save: "Save",
  cancel: "Cancel",
  close: "Close",
  delete: "Delete",
  edit: "Edit",
  back: "Back",
  next: "Next",
  loading: "Loading…",
  error: "Error",
  success: "Success",
  confirm: "Confirm",
  yes: "Yes",
  no: "No",
  or: "or",
  and: "and",
  submit: "Submit",
  send: "Send",
  retry: "Retry",
  required: "Required",
  optional: "Optional",
  search: "Search",
  no_results: "No results found",
  select: "Select",
  selected: "Selected",
  all: "All",
  none: "None",
  show_more: "Show more",
  show_less: "Show less",
  read_more: "Read more",
  copied: "Copied!",
  copy: "Copy",
  download: "Download",
  upload: "Upload",
  thinking: "Thinking...",
  waiting: "Waiting…",
  section_error: "Section error",
},

// ─── Interview Assistant ─────────────────────────────────
interview_copilot: {
  page_title: "Interview Assistant",
  page_subtitle: "Practice interviews and get real-time AI guidance tailored to your target role.",
  live_page_title: "Live Interview Assistant",
  live_page_subtitle: "Get private, real-time AI guidance during an actual interview.",
  choose_mode: "Choose interview mode",
  end_before_switching_mode: "End the current session before switching interview modes.",
  // Nav / promotion
  nav_shortcut: "Interview Assistant",
  promo_title: "Interview Assistant",
  promo_desc: "Practice interviews with live AI feedback — tailored to your resume and target role.",
  promo_cta: "Try Interview Assistant",
  promo_badge: "New",
  // Eligibility
  eligible_badge: "Premium",
  not_eligible_badge: "Upgrade required",
  eligible_desc: "You have full access to Interview Assistant.",
  not_eligible_desc: "Interview Assistant requires an active paid subscription.",
  upgrade_cta: "Upgrade now",
  // Profile & resume
  profile_section: "Profile & Resume",
  profile_desc: "Select the resume profile to use for this interview session.",
  no_profile_title: "No resume profiles found",
  no_profile_desc: "Add a resume and target role before starting a session.",
  go_to_resume: "Set up resume",
  selected_profile: "Selected profile",
  profile_label: "Profile",
  target_role: "Target role",
  no_target_role: "No target role set",
  change: "Change",
  // Mic
  mic_section: "Microphone",
  mic_desc: "Allow microphone access for voice-based interview questions.",
  mic_check_btn: "Check microphone",
  mic_granted: "Microphone ready",
  mic_denied: "Access denied — you can still use text input.",
  mic_optional_hint: "Microphone is optional. You can use text input only.",
  // Session
  session_section: "Interview Session",
  start_session: "Start Interview",
  end_session: "End Session",
  session_active_label: "Session active",
  session_ended_label: "Session ended",
  session_new: "Start new session",
  type_placeholder: "Type a question or your answer here…",
  submit_btn: "Send",
  you_label: "You",
  ai_label: "AI",
  session_empty: "Your session transcript will appear here.",
  thinking: "AI is thinking…",
  retry_btn: "Retry",
  clear_transcript: "Clear",
  clear_confirm: "Clear this session's transcript? This cannot be undone.",
  // Audio
  record_btn: "Record an audio answer",
  record_stop: "Stop recording",
  recording_label: "Recording… click the stop button to send.",
  processing_audio: "Processing your audio…",
  audio_answer_label: "🎤 Audio answer",
  audio_error: "Failed to process audio. Please try again or use text input.",
  audio_no_clear_speech: "We couldn't hear clear speech from your microphone. End this session and open Device Check, select another microphone, and use Play Back to confirm the sound. If the meter still does not move or the recording sounds muffled, try a headset or another device.",
  audio_unsupported: "Audio recording is not supported in this browser — you can still use text input.",
  // History
  history_section: "Session History",
  history_empty: "No previous sessions yet.",
  session_label: "Session",
  view_summary_btn: "View summary",
  score_label: "Score",
  // States
  loading: "Loading…",
  starting: "Starting session…",
  ending: "Ending session…",
  error_start: "Failed to start session. Please try again.",
  error_end: "Failed to end session. Please try again.",
  error_submit: "Failed to send message. Please try again.",
  error_load: "Failed to load interview data.",
  error_clear: "Failed to clear the session. Please try again.",
  error_auth: "Your session has expired. Please sign in again.",
  error_rate_limit: "You're sending messages too quickly. Please wait a moment and try again.",
  error_network: "Network error. Please check your connection and try again.",
  error_unavailable: "Interview Assistant is temporarily unavailable. Please try again later.",
  error_session_expired: "This interview session has expired. Please start a new session.",
  unavailable_badge: "Unavailable",
  // Validation
  requires_profile: "Please select a resume profile before starting.",
  requires_target_role: "Please set a target role on your selected resume profile.",
  requires_subscription: "An active subscription is required to use Interview Assistant.",
  monthly_limit_reached: "You have reached your monthly Interview Assistant minute limit.",
  // Profile selector labels
  profile_slot_1: "Primary Resume Profile",
  profile_slot_2: "Resume Profile 2",
  profile_slot_3: "Resume Profile 3",
  profile_slot_4: "Resume Profile 4",
  profile_slot_5: "Resume Profile 5",
  // Landing / workspace states
  start_copilot_cta: "Start Interview Assistant",
  landing_desc: "Set up your resume and target job, then practice a live interview with AI guidance.",
  begin_live_session: "Begin Live Session",
  single_session_note: "Only one Interview Assistant session can be active at a time. If another device is active, we’ll help you move the interview here safely.",
  active_elsewhere: "An interview is already active in {surface}.",
  move_interview_explanation: "Moving it here safely ends billing on the previous surface and starts this one. Your account remains connected everywhere.",
  move_interview_here: "Move Interview Here",
  moving_interview: "Moving interview…",
  interview_moved_here: "The previous interview ended safely. Starting it here now.",
  complete_setup_to_move: "Open and complete the résumé, target job, and device setup before moving the interview here.",
  sign_in_again: "Sign In Again",
  manage_subscription: "Manage Subscription",
  start_new_interview: "Start New Interview",
  cancel_btn: "Cancel",
  back_to_landing: "Back",
  // Setup wizard
  setup_title: "Resume & Job Setup",
  setup_step_resume: "Resume",
  setup_step_job: "Target Job",
  setup_step_review: "Review",
  setup_step_device: "Device Check",
  step_done: "Done",
  step_todo: "To do",
  continue_btn: "Continue",
  back_btn: "Back",
  edit_btn: "Edit",
  // Resume step
  resume_source_existing: "Use an existing Jobolar resume",
  resume_source_upload: "Upload a new resume",
  existing_resume_pick: "Select one of your saved resume profiles.",
  no_jobolar_resumes: "You don't have any saved resumes yet — upload one below.",
  resume_slot_label: "Resume slot {n}",
  resume_selected_badge: "Selected",
  upload_drop_hint: "Drag & drop your resume here, or",
  upload_browse: "browse files",
  upload_accept_hint: "PDF, DOCX, or TXT, up to 10 MB.",
  upload_error_type: "That file format is not supported. Upload a PDF, DOCX, or TXT file, or paste the résumé text instead.",
  upload_error_size: "File is too large. The maximum size is 10 MB.",
  upload_error_failed: "Upload failed. Please try again.",
  upload_progress_label: "Uploading… {pct}%",
  upload_saved_note: "Saved to your Jobolar resume profiles (slot {n}).",
  upload_not_saved_note: "All 5 resume slots are full — this resume will be used for this interview only.",
  extracting_text: "Reading your resume…",
  extract_failed: "We couldn't process that résumé file. Upload a different PDF, DOCX, or TXT file, or paste the résumé text instead.",
  resume_name_label: "Resume name",
  resume_name_placeholder: "e.g. Product Manager resume",
  candidate_preview_title: "Extracted candidate information",
  candidate_preview_hint: "Review and correct the details we detected before continuing.",
  candidate_name_label: "Candidate name",
  candidate_email_label: "Email",
  candidate_phone_label: "Phone",
  resume_text_label: "Resume text preview",
  confirm_resume_btn: "Use this resume",
  resume_ready_badge: "Resume ready",
  remove_resume_btn: "Remove",
  // Target job step
  job_title_label: "Target job title",
  job_title_placeholder: "e.g. Senior Data Analyst",
  company_label: "Company (optional)",
  company_placeholder: "e.g. Acme Corp",
  jd_label: "Job description",
  jd_placeholder: "Paste the job description here…",
  jd_upload_btn: "Upload job description file",
  jd_file_error: "We couldn't process that file. Upload a different PDF, DOCX, or TXT file, or paste the job description instead.",
  jd_min_hint: "Paste the job description or at least a short summary of the role.",
  interview_type_label: "Interview type",
  type_general: "General",
  type_behavioral: "Behavioral",
  type_technical: "Technical",
  type_role_specific: "Role-specific",
  experience_label: "Experience level (optional)",
  exp_unspecified: "Not specified",
  exp_entry: "Entry level",
  exp_mid: "Mid level",
  exp_senior: "Senior",
  exp_lead: "Lead / Manager",
  focus_label: "Interview focus areas (optional)",
  focus_placeholder: "e.g. leadership, SQL, stakeholder management",
  // Review step
  review_section: "Review Context",
  review_resume: "Resume",
  review_candidate: "Candidate",
  review_role: "Target role",
  review_company: "Company",
  review_jd: "Job description",
  review_type: "Interview type",
  review_focus: "Focus areas",
  review_experience: "Experience level",
  not_provided: "Not provided",
  // Device check
  device_section: "Microphone & Device Check",
  mic_device_label: "Selected microphone",
  mic_device_unknown: "Default microphone",
  test_record_btn: "Record a short test",
  test_stop_btn: "Stop test",
  test_play_btn: "Play back",
  test_recording_label: "Recording test… speak now.",
  test_ready_label: "Test recording ready — play it back to check your mic.",
  text_only_label: "Text-only mode",
  text_only_hint: "Skip the microphone and answer with text only.",
  // Start requirements
  needs_resume: "Select or upload a resume to continue.",
  needs_job_title: "Enter a target job title to continue.",
  needs_job_description: "Provide a job description or role summary to continue.",
  syncing_profile: "Preparing your interview profile…",
  // Live workspace
  elapsed_label: "Elapsed",
  context_resume_label: "Resume",
  context_role_label: "Role",
  end_confirm: "End this interview session?",
  summary_title: "Session summary",
  summary_loading: "Generating your session summary…",
  summary_unavailable: "No summary is available for this session.",
  // Fallback / coming soon
  coming_soon: "Interview Assistant is available now.",
  coming_soon_desc: "Choose Live Interview Assistant or Mock Interview, then set up your r\u00e9sum\u00e9, target role, and device.",
  coming_soon_notify: "Available on Jobolar Web, the Chrome extension, and the Desktop app.",
  guidance_waiting: "Guidance will appear after the interviewer asks a question.",
  panel_transcribed_question: "Transcribed Question",
  panel_talking_points: "Key Points",
  panel_sample_answer: "Full Answer",
  panel_answer_structure: "Answer Structure",
  panel_follow_up_questions: "Possible Follow-Up Questions",
  panel_interview_insights: "Interview Insights",
  keywords_label: "Keywords",
  confidence_score_label: "Confidence score",
  speaking_time_label: "Estimated speaking time",
  copy_action: "Copy",
  expand_action: "Expand",
  open_separate_action: "Open separately",
  close_expanded_panel: "Close expanded panel",
  answer_mode: "Answer mode",
  saving_mode: "Saving mode…",
  coach_idle: "Coach idle",
  changes_next_question: "Changes apply to the next question.",
  resume_grounded: "Résumé-grounded",
  resume_grounded_desc: "Use my résumé and target role to personalize guidance.",
  practice_mode: "Practice mode",
  practice_mode_desc: "Use a sample interview question when no live question is available.",
  stop_listening: "Stop listening",
  processing_question: "Processing question…",
  start_listening: "Start listening",
  keyboard_listening_hint: "Press Space to start or stop listening.",
  mic_waveform_title: "Live microphone",
  mic_waveform_subtitle: "Your audio stays active only while listening.",
  mic_waveform_aria: "Live microphone audio level",
  regenerate_answer: "Regenerate answer",
  shorter_answer: "Make it shorter",
  detailed_answer: "Add more detail",
  more_technical: "Make it more technical",
  more_technical_directive: "Regenerate the answer with more technical detail and role-specific terminology.",
  different_example: "Use a different example",
  different_example_directive: "Regenerate the answer using a different relevant example.",
  copy_answer: "Copy answer",
  copy_everything: "Copy everything",
  clear_current_question: "Clear current question",
  export_question: "Question",
  export_talking_points: "Suggested talking points",
  export_follow_ups: "Possible follow-up questions",
  transcript_aria: "Interview transcript",
  manual_question_entry: "Type the interviewer's question",
  question_input: "Question input",
  question_input_audio: "Audio",
  question_input_text: "Text",
  question_input_image: "Image",
  question_text_help: "Type or paste the complete interviewer question. Code formatting is preserved.",
  question_characters: "{count} of 6,000 characters",
  question_image_help: "Paste or choose a PNG, JPEG or WebP screenshot. Your explicit paste or upload starts temporary text extraction and answer preparation automatically; the image is not saved.",
  question_image_choose: "Choose screenshot",
  question_image_paste: "Paste an image here",
  question_image_extract: "Extract question",
  question_image_extracting: "Reading screenshot…",
  question_image_confirm: "The extracted question is processed automatically after your paste, upload, or capture action.",
  question_image_invalid: "Choose a PNG, JPEG or WebP image smaller than 4 MB.",
  question_image_error: "The screenshot could not be read. Try a clearer PNG or JPEG, or type the question instead.",
  process_question: "Process Question",
  audio_input_help: "Manual Listen captures the interviewer until you press Stop or Space. Smart Listen monitors automatically when enabled.",
  history_clear_confirm: "Clear all session history? This cannot be undone.",
  history_clear_error: "Failed to clear session history. Please try again.",
  device_test_required: "Complete the microphone check or choose text-only mode before continuing.",
  mode_change_error: "We couldn't save that mode. Please try again.",
  dismiss: "Dismiss",
  monthly_interview_time: "Monthly Interview Time",
  pasted_resume_name: "Pasted résumé",
  fresh_resume_notice: "Use a current résumé so the guidance reflects your latest experience.",
  paste_resume_label: "Paste résumé text",
  paste_resume_placeholder: "Paste your résumé text here…",
  paste_resume_note: "Pasted text is used for this interview session.",
  or_upload_file: "or upload a file",
  candidate_profile_label: "Candidate profile",
  candidate_profile_hint: "Optional, but strongly recommended: these details make your interview answers more personal and accurate.",
  mic_error_permission: "Microphone access was denied. Allow access in your browser settings or use text-only mode.",
  mic_error_missing: "No microphone was found. Connect one or use text-only mode.",
  mic_error_busy: "Your microphone is being used by another app. Close it there and try again.",
  mic_error_system: "We couldn't access your microphone. Check your browser and system permissions.",
  mic_help_title: "Microphone permission help",
  mic_help_subtitle: "Follow the instructions for your browser and computer.",
  mic_help_close: "Close microphone help",
  mic_detected_problem: "Microphone issue detected",
  mic_help_windows: "Windows: Settings → Privacy & security → Microphone. Turn on microphone access for apps and your browser.",
  mic_help_macos: "macOS: System Settings → Privacy & Security → Microphone. Enable access for your browser.",
  mic_help_chrome_title: "Chrome or Edge",
  mic_help_chrome: "Select the lock icon beside the address, set Microphone to Allow, then reload this page.",
  mic_help_safari: "Safari: Safari → Settings for This Website → Microphone → Allow, then reload.",
  mic_help_firefox: "Firefox: select the permission icon beside the address and allow microphone access.",
  mic_help_note: "You can always continue in text-only mode without microphone access.",
  mic_test_too_short: "The recording was too short. Record for at least three seconds.",
  mic_test_silent: "We couldn't detect your voice. Check the selected microphone and try again.",
  mic_playback_error: "We couldn't play the test recording.",
  mic_playback_blocked: "Playback was blocked. Select Play test recording to listen.",
  mic_test_passed_begin: "Microphone test passed. You can begin the live session.",
  mic_access_allowed_test: "Microphone access is allowed. Record a short test to confirm the sound.",
  mic_requesting: "Requesting microphone access…",
  mic_readiness_title: "Check your microphone",
  mic_readiness_desc: "Record and play back a short sample before your interview.",
  mic_permission_note: "Your browser will ask for microphone permission.",
  mic_help_button: "Microphone help",
  mic_view_help: "View permission help",
  mic_device_fallback: "System default microphone",
  mic_unsupported_next: "Recording is not supported in this browser. Continue with text-only mode.",
  mic_recording_test: "Recording test…",
  mic_speak_normally: "Speak normally for at least three seconds.",
  mic_record_again: "Record again",
  mic_record_three_seconds: "Record a 3-second test",
  mic_playing_test: "Playing test recording…",
  mic_playback_instruction: "Click Play back and confirm you can hear your recording before continuing.",
  mic_test_another: "Test another recording",
  mic_hear_question: "Could you hear your voice clearly?",
  mic_hear_yes: "Yes, it sounds clear",
  mic_hear_no_error: "Select another microphone or check your permissions, then record again.",
  mic_hear_no: "No, try again",
  mic_voice_ready: "Your microphone is ready.",
},

// ─── AI Interview Assistant product, billing & legal ────────
copilot_product: {
  copilot_only_badge: "Standalone plan",
  copilot_only_name: "AI Interview Assistant Only",
  copilot_only_desc: "Real-time AI guidance for live interviews, without the job-application automation plan.",
  copilot_feature_realtime: "Real-time interview guidance",
  copilot_feature_transcription: "Live question transcription",
  copilot_feature_modes: "Text and microphone modes",
  copilot_feature_waveform: "Live microphone waveform",
  copilot_feature_reset: "Minutes reset monthly",
  choose_hours_month: "Choose hours per month",
  decrease_hours_aria: "Decrease hours",
  increase_hours_aria: "Increase hours",
  hours_input_aria: "Interview Assistant hours per month",
  hour_per_month: "hour per month",
  hours_per_month: "hours per month",
  per_hour: "per hour",
  save_percent: "Save 60%",
  copilot_minutes_count: "{minutes} AI Interview Assistant minutes",
  copilot_minutes_every_month: "{minutes} AI Interview Assistant minutes every month",
  minutes_count: "{minutes} minutes",
  every_month: "every month",
  prepaid_term_hours: "{hours} hours included across the {term}",
  prepaid_term_monthly: "one-month term",
  prepaid_term_quarterly: "three-month term",
  prepaid_term_semiannual: "six-month term",
  due_for_term: "{amount} billed {term}",
  billing_term_monthly: "every month",
  billing_term_quarterly: "every 3 months",
  billing_term_semiannual: "every 6 months",
  tax_no_rollover: "Applicable tax is added separately. Unused minutes do not roll over.",
  choose_copilot_only: "Choose AI Interview Assistant Only",
  no_hidden_fees_reset: "No hidden fees. Your selected minutes reset each month.",
  confirm_copilot_title: "Quick Confirmation Before You Continue",
  confirm_copilot_subtitle: "Please confirm the following before purchasing AI Interview Assistant:",
  confirm_copilot_1: "I understand that AI Interview Assistant provides real-time interview guidance and suggested responses, but it does not guarantee interview success, job offers, or employment.",
  confirm_copilot_2: "I understand that AI-generated responses may not always be accurate, complete, or appropriate, and I am responsible for reviewing and adapting them before speaking.",
  confirm_copilot_3: "I understand that I should follow the interviewer’s, employer’s, and interview platform’s rules regarding the use of AI assistance.",
  confirm_copilot_4: "I understand that microphone access is optional, and I can use text-only mode instead.",
  confirm_copilot_5: "I understand that the quality of the guidance may vary depending on audio quality, internet connection, interview questions, my résumé, and the information I provide.",
  confirm_copilot_6: "I understand that unused AI Interview Assistant minutes do not roll over to the next month.",
  confirm_copilot_continue: "I Understand — Continue to Payment",
  close: "Close",
  copilot_faq_q: "What is AI Interview Assistant Only?",
  copilot_faq_a: "It is a standalone plan for real-time interview support. Choose 1–200 whole hours per month, use text or microphone input, and receive AI guidance in your selected language. Minutes reset monthly and unused minutes do not roll over.",
  product_label: "AI Interview Assistant",
  product_title: "Real-time guidance for your live interview",
  product_hero_desc: "Hear or type the interviewer's question and get structured, résumé-aware guidance in the language you selected.",
  product_open: "Open Interview Assistant",
  product_see_how: "See how it works",
  product_live_waveform: "Live microphone waveform",
  product_text_entry: "Text question entry",
  product_monthly_timer: "Monthly time tracking",
  product_tools_title: "Everything you need during the interview",
  product_benefit_1_title: "Real-time guidance",
  product_benefit_1_desc: "Turn each question into focused talking points and a natural sample answer.",
  product_benefit_2_title: "Résumé-aware answers",
  product_benefit_2_desc: "Ground suggestions in your experience, target role, and job description.",
  product_benefit_3_title: "Speak or type",
  product_benefit_3_desc: "Use multilingual microphone transcription or enter the question manually.",
  product_benefit_4_title: "Interview insights",
  product_benefit_4_desc: "See answer structure, likely follow-ups, keywords, confidence, and speaking time.",
  product_how_title: "How Interview Assistant works",
  product_step_1_title: "Add your context",
  product_step_1_desc: "Select a résumé and add the role and job description.",
  product_step_2_title: "Listen or type",
  product_step_2_desc: "Let Assistant transcribe the question or enter it yourself.",
  product_step_3_title: "Answer with confidence",
  product_step_3_desc: "Use the generated guidance, adapt it, and respond in your own voice.",
  product_included_title: "Included in Jobolar plans",
  product_60_minutes: "60 minutes per month",
  product_150_minutes: "150 minutes per month",
  product_300_minutes: "300 minutes per month",
  product_copilot_only: "Assistant-only plan",
  product_variable_minutes: "Choose 60–12,000 minutes per month",
  product_disclaimer: "AI guidance may be inaccurate. Follow all applicable interview, employer, platform, privacy, and consent rules.",
  feature_card_title: "AI Interview Assistant",
  feature_card_desc: "Get real-time, multilingual talking points, sample answers, and interview insights.",
  how_copilot_title: "Use AI Interview Assistant",
  how_copilot_desc: "Prepare your context, capture each live question, and receive guidance in your selected language.",
  how_copilot_step_1: "Choose your résumé, target role, and interview language.",
  how_copilot_step_2: "Use your microphone or type the interviewer's question.",
  how_copilot_step_3: "Review and adapt the suggested talking points and answer.",
  roadmap_phase_label: "New product",
  roadmap_title: "AI Interview Assistant",
  roadmap_target: "Available now",
  roadmap_desc: "A live interview workspace that understands spoken or typed questions and returns structured guidance.",
  roadmap_bullet_1: "Multilingual speech transcription and text input",
  roadmap_bullet_2: "Résumé-grounded answers and follow-up preparation",
  roadmap_bullet_3: "Monthly time controls and session history",
  roadmap_highlight: "Use it inside a Jobolar plan or purchase Assistant on its own.",
  nav_interview_copilot: "Interview Assistant",
  terms_copilot_title: "AI Interview Assistant",
  terms_copilot_li1: "AI Interview Assistant provides real-time transcription, suggested talking points, sample answers, and other interview guidance using audio, text, résumé information, and context you choose to provide.",
  terms_copilot_li2: "Generated content may be inaccurate, incomplete, delayed, or unsuitable. You are responsible for reviewing, adapting, and deciding whether to use any suggestion.",
  terms_copilot_li3: "The service does not guarantee interview performance, job offers, employment, or any particular outcome.",
  terms_copilot_li4: "You must comply with applicable laws and with the interviewer’s, employer’s, recruiting platform’s, and interview platform’s rules concerning recording, transcription, confidentiality, and AI assistance. You are responsible for obtaining any required consent before capturing or processing another person’s speech.",
  terms_copilot_li5: "You may not use the service to misrepresent your identity, qualifications, experience, or authorization, or to facilitate cheating, fraud, unlawful surveillance, or other deceptive or prohibited conduct.",
  terms_copilot_li6: "Microphone access is optional; text-only mode may be available. Audio quality, connectivity, device permissions, and third-party service availability can affect performance.",
  terms_copilot_li7: "Assistant allowances are measured in minutes per month. Unused monthly minutes do not roll over. Quarterly and semiannual purchases may be prepaid for the full term while the selected allowance is made available monthly.",
  terms_copilot_li8: "The Chrome Translucent Focus Overlay is a visible browser window and may appear in screen shares or recordings. It is not the screen-share-invisible Desktop experience. You are responsible for testing your exact meeting, browser, operating-system, and sharing setup before use.",
  privacy_copilot_title: "AI Interview Assistant Data",
  privacy_copilot_intro: "When you use AI Interview Assistant, we may collect and process the information needed to provide and secure the feature, including:",
  privacy_copilot_li1: "microphone audio, typed text, an image you explicitly upload, or a locally cropped question-area capture you explicitly take during a live session;",
  privacy_copilot_li2: "audio- or image-derived transcripts, interview questions, generated talking points and answers, session history, timestamps, mode selections, and usage-minute records;",
  privacy_copilot_li3: "résumés, job descriptions, preferences, and other context you choose to use for résumé-grounded guidance; and",
  privacy_copilot_li4: "device, browser, permission, connectivity, diagnostic, and error information used to operate and troubleshoot the feature.",
  privacy_copilot_use: "We use this information to transcribe questions, generate and personalize guidance, maintain session history, enforce purchased allowances, prevent abuse, provide support, and improve reliability and safety.",
  privacy_copilot_providers: "Audio, cropped question images, transcripts, prompts, and related context may be transmitted to service providers that supply hosting, speech recognition, optical-character recognition, artificial-intelligence processing, monitoring, or security services. Those providers process information on our behalf under their applicable terms and safeguards.",
  privacy_copilot_permission: "Microphone and display-capture permissions are controlled by your browser and operating system. You can decline them and use text-only or uploaded-image input where available. A display source is requested only after you choose Capture Question Area; your browser requires you to choose a tab, window, or screen whenever sharing starts, and JOBolar.Ai cannot silently grant or permanently remember that permission. Do not capture another person’s voice, image, or confidential content unless you have any consent and authorization required by applicable law and the relevant interview, employer, and platform rules.",
  privacy_copilot_screen_title: "Web question-area screen capture",
  privacy_copilot_screen_capture: "The Web Interview Assistant does not take screenshots until you explicitly select Capture question. Your browser locally decodes the chosen tab, window, or screen and JOBolar.Ai shows a live selector preview. Only the selected pixels are copied into a crop-sized local canvas and sent temporarily for text extraction; the extracted question then requests an answer automatically. Complete display frames are not encoded, uploaded, or stored. Normalized crop coordinates last for the current interview, or remain in this browser only when you select the optional reuse setting; cropped image previews are cleared after processing.",
  privacy_copilot_retention: "Assistant records are retained only as reasonably necessary to provide the service, maintain session history and billing records, meet legal obligations, resolve disputes, and protect the service. You may request access or deletion as described in the Your Rights section, subject to legal and operational retention requirements.",
  privacy_chrome_title: "Chrome Web Store Interview Assistant disclosure",
  privacy_chrome_capture: "The Chrome Interview Assistant does not process or upload screen video and does not upload candidate microphone audio. For browser meetings, it uses audio from the selected meeting tab. For installed Zoom or Teams, the user may explicitly open Chrome’s screen/window picker to share compatible app/window or system audio. Chrome supplies a required display video track with that stream; JOBolar disables it immediately and never renders it, reads its pixels, records it, encodes it, processes it, or uploads screen video. System audio may include notifications or other applications, so users should close or mute unrelated apps. Manual Listen sends a selected-source audio segment when the user stops it, or automatically when an unfinished segment reaches the five-minute safety limit. If the user enables optional Smart Listen, local voice detection automatically sends short detected-speech segments from the selected source for private transcription. A question image or selected screenshot crop is sent only after the user explicitly pastes, chooses, or captures it; JOBolar temporarily extracts the question and automatically requests an answer, then discards the image. The visible Translucent Focus Overlay is injected into the selected browser meeting tab. Adjusting answer-background transparency does not hide it from screen sharing or recording.",
  privacy_chrome_limited_use: "JOBolar's use of information received from Chrome APIs complies with the Chrome Web Store User Data Policy, including its Limited Use requirements.",
  refund_copilot_title: "AI Interview Assistant Purchases",
  refund_copilot_li1: "Assistant-only subscriptions provide the number of AI Interview Assistant minutes shown at checkout each month. Unused monthly minutes expire and do not roll over.",
  refund_copilot_li2: "Quarterly and semiannual plans may be charged in advance for the full term even though minutes are released monthly. Canceling stops the next renewal and does not ordinarily refund the current prepaid term, except where required by law or expressly approved under this policy.",
  refund_copilot_li3: "Minutes already used are non-refundable. If a refund is approved after partial use, JOBolar.Ai may deduct the value of consumed minutes or provide a proportionate refund where permitted by law.",
  refund_copilot_li4: "Duplicate charges, incorrect quantities, or confirmed extended service failures may be reviewed individually. A refund is not guaranteed for device-permission problems, unsupported equipment, poor internet or audio quality, failure to follow setup instructions, dissatisfaction with AI-generated guidance, or an unsuccessful interview.",
  refund_copilot_li5: "Cancel before the renewal date to avoid the next charge. Losing access to, or choosing not to use, available minutes does not by itself create a right to a refund.",
},

// ─── Dashboard ──────────────────────────────────────────
dashboard: {
  title: "Dashboard",
  welcome: "Welcome, {name}.",
  workspace_eyebrow: "Your JOBolar workspace",
  continue_search: "Continue job search",
  private_workspace: "Private by design",
  profile_ready: "Career profile connected",
  search_workspace: "Job Search Workspace",
  search_workspace_desc: "Manage your career profile, résumé versions, preferences, certificates, Gmail connection, and Smart Apply setup in one place.",
  open_workspace: "Open workspace",
  interview_workspace: "Interview Studio",
  interview_workspace_desc: "Choose live guidance or a mock interview, then use the same résumé, job context, microphone checks, and core controls.",
  choose_mode: "Choose interview mode",
  analytics_workspace: "Application Analytics",
  analytics_workspace_desc: "Review application activity, source and method performance, saved answers, and account-level automation controls.",
  view_activity: "View activity dashboard",
},

// ─── Contact Us ────────────────────────────────────────
contact: {
  title: "Contact Us",
  subtitle: "We'd love to hear from you. Follow us and say hello.",
  social: "Social",
  office: "Office Location",
  name_placeholder: "Your name",
  email_placeholder: "Your email",
  message_placeholder: "Message",
  send: "Send",
},

// ─── AI Buddy / Assistant ──────────────────────────────
assistant: {
  title: "DeepSeek Assistant",
  powered_by: "Powered by DeepSeek's {mode} model.",
  enable_reasoning: "Enable reasoning mode",
  greeting: "Hi! I'm JOBolar's DeepSeek assistant. Ask me anything about your job search, resumes, or interview prep, and I'll do my best to help.",
  input_placeholder: "Type your message here…",
  unavailable: "AI assistant unavailable. We'll be back with a new assistant soon.",
  daily_limit: "You have reached your daily limit of {max} prompts. New prompts will be available after 24 hours of your last input.",
  error: "Something went wrong while talking to DeepSeek. Please try again in a few moments.",
  api_note: "DeepSeek API key is stored securely on the server. Reasoning mode may take a little longer to respond.",
  // AI Chatty Buddy (Onboarding AI)
  chatty_buddy: "AI Chatty Buddy",
  chatty_desc: "Your AI guide for onboarding",
  // Legal Buddy
  legal_buddy: "AI Legal Buddy",
  legal_desc: "Your legal document assistant",
  legal_q1: "What does the refund window cover?",
  legal_q2: "How does JOBolar handle my data?",
  legal_q3: "When can JOBolar suspend access?",
  // Onboarding Buddy
  onboarding_q1: "Review this attachment for a Data Analyst role.",
  onboarding_q2: "Give me 5 prioritized edits for impact.",
  onboarding_q3: "Score my resume (A–F) with 3 reasons.",
  daily_limit_buddy: "You have reached your daily limit of {max} prompts. New prompts will be available after 24 hours of your last input.",
},

// ─── OAuth Badge ───────────────────────────────────────
oauth: {
  badge_title: "Google OAuth Verified",
  badge_desc: "JOBolar has been approved by Google for secure use of Gmail Send API.",
},

// ─── Error Boundary ────────────────────────────────────
errors: {
  section_error: "Section error",
  debug_client: "Client Error (DebugErrorOverlay)",
  debug_disable: "Disable by unsetting NEXT_PUBLIC_DEBUG_OVERLAY.",
},
};

export default en;
